// @ts-nocheck
// TODO: tighten types after migration
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import API_BASE_URL from '../config/api';
import { getAuthToken } from '../utils/auth';
import './DynamicField.css';

// Helper function to detect field type
function getType(value) {
    if (Array.isArray(value)) return 'array';
    if (value === null || value === undefined) return 'null';
    if (typeof value === 'object') return 'object';
    return typeof value;
}

// Tag Input Component for Arrays (like SEO Keywords)
function TagInput({ value = [], onChange, placeholder = "Add items..." }) {
    const [inputValue, setInputValue] = useState('');

    const processInput = () => {
        if (inputValue.trim()) {
            // Split by comma and process all values
            const newValues = inputValue
                .split(',')
                .map(item => item.trim())
                .filter(item => item.length > 0) // Remove empty strings
                .filter(item => !value.includes(item)); // Avoid duplicates
            
            if (newValues.length > 0) {
                onChange([...value, ...newValues]);
                setInputValue('');
            } else {
                // Clear input even if no new values (user might have typed duplicates)
                setInputValue('');
            }
        }
    };

    const handleAdd = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            processInput();
        }
    };

    const handleBlur = (e) => {
        // Process values when user clicks outside the input field
        // But don't process if clicking on the Clear button or remove button
        const relatedTarget = e.relatedTarget;
        if (relatedTarget && (relatedTarget.type === 'button' || relatedTarget.closest('button'))) {
            // User clicked on a button, don't process
            return;
        }
        processInput();
    };

    const handleRemove = (index) => {
        onChange(value.filter((_, i) => i !== index));
    };

    return (
        <div className="tag-input-container">
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '4px',
                padding: '4px 6px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                minHeight: '24px',
                backgroundColor: 'white',
                alignItems: 'center'
            }}>
                {value.map((item, index) => (
                    <span
                        key={index}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px',
                            padding: '2px 6px',
                            backgroundColor: '#eff6ff',
                            border: '1px solid #bfdbfe',
                            borderRadius: '4px',
                            fontSize: '12px',
                            color: '#1e40af',
                            height: '24px',
                            lineHeight: '20px'
                        }}
                    >
                        {String(item)}
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleRemove(index);
                            }}
                            onMouseDown={(e) => {
                                // Prevent input blur when clicking remove button
                                e.preventDefault();
                            }}
                            style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#1e40af',
                                fontSize: '14px',
                                lineHeight: 1,
                                padding: 0,
                                marginLeft: '2px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '14px',
                                height: '14px'
                            }}
                            title="Remove"
                        >
                            ×
                        </button>
                    </span>
                ))}
                <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleAdd}
                    onBlur={handleBlur}
                    placeholder={placeholder}
                    style={{
                        border: 'none',
                        outline: 'none',
                        flex: 1,
                        minWidth: '120px',
                        fontSize: '13px',
                        padding: '4px 0'
                    }}
                />
            </div>
            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                Enter comma-separated values and press Enter or click outside to process. {value.length} item{value.length !== 1 ? 's' : ''}
            </div>
        </div>
    );
}

// Image Preview Component for coverImage objects
function ImagePreviewField({ value = {}, onChange, label, onClear }) {
    const handleChange = (field, newValue) => {
        onChange({
            ...value,
            [field]: newValue
        });
    };

    return (
        <div>
            {value && value.url && (
                <div style={{ marginBottom: '12px' }}>
                    <img
                        src={value.url}
                        alt={(value && value.imageTitle) || label || 'Image'}
                        style={{
                            maxWidth: '300px',
                            maxHeight: '200px',
                            borderRadius: '6px',
                            border: '1px solid #d1d5db',
                            objectFit: 'contain'
                        }}
                        onError={(e) => {
                            e.target.style.display = 'none';
                        }}
                    />
                </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#6b7280' }}>
                        Image URL
                    </label>
                    <input
                        type="text"
                        value={(value && value.url) || ''}
                        onChange={(e) => handleChange('url', e.target.value)}
                        placeholder="https://example.com/image.jpg"
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px'
                        }}
                    />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#6b7280' }}>
                        File Name
                    </label>
                    <input
                        type="text"
                        value={(value && value.fileName) || ''}
                        onChange={(e) => handleChange('fileName', e.target.value)}
                        placeholder="image.jpg"
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px'
                        }}
                    />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#6b7280' }}>
                        Image Title (Alt Text)
                    </label>
                    <input
                        type="text"
                        value={(value && value.imageTitle) || ''}
                        onChange={(e) => handleChange('imageTitle', e.target.value)}
                        placeholder="Descriptive title"
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px'
                        }}
                    />
                </div>
            </div>
        </div>
    );
}

// ── ProfilesEditor ─────────────────────────────────────────────────────────────
// Renders an array of { platform, url, handle } objects as editable cards.
const PROFILE_PLATFORMS = ['bluesky', 'facebook', 'instagram', 'linkedin', 'other', 'substack', 'threads', 'truthsocial', 'twitter', 'youtube'];
const PLATFORM_ICONS = { twitter: '𝕏', truthsocial: 'T', instagram: '📸', youtube: '▶', substack: '📝', linkedin: '🔗', bluesky: '🦋', threads: '@', facebook: 'f', other: '🌐' };

function ProfilesEditor({ value = [], onChange, readOnly = false }) {
    const [editingIdx, setEditingIdx] = useState(null);
    const [draft, setDraft] = useState({ platform: 'twitter', url: '', handle: '' });
    const [adding, setAdding] = useState(false);

    const profiles = Array.isArray(value) ? value : [];

    function startEdit(i) {
        setEditingIdx(i);
        setDraft({ platform: profiles[i].platform || 'twitter', url: profiles[i].url || '', handle: profiles[i].handle || '' });
        setAdding(false);
    }

    function saveEdit(i) {
        const updated = profiles.map((p, idx) => idx === i ? { ...draft } : p);
        onChange(updated);
        setEditingIdx(null);
    }

    function deleteEntry(i) {
        onChange(profiles.filter((_, idx) => idx !== i));
        if (editingIdx === i) setEditingIdx(null);
    }

    function startAdd() {
        setAdding(true);
        setDraft({ platform: 'twitter', url: '', handle: '' });
        setEditingIdx(null);
    }

    function confirmAdd() {
        if (!draft.url && !draft.handle) return;
        onChange([...profiles, { ...draft }]);
        setAdding(false);
        setDraft({ platform: 'twitter', url: '', handle: '' });
    }

    const rowStyle = {
        display: 'grid', gridTemplateColumns: '1fr 2fr 1fr auto', gap: '8px',
        alignItems: 'center', padding: '8px 10px', borderRadius: '6px',
        border: '1px solid #e5e7eb', background: '#f9fafb', marginBottom: '6px',
    };
    const inputStyle = {
        padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: '5px',
        fontSize: '13px', width: '100%', boxSizing: 'border-box',
    };
    const btnStyle = (color) => ({
        padding: '4px 10px', borderRadius: '5px', border: 'none', cursor: 'pointer',
        fontSize: '12px', fontWeight: 600, background: color, color: '#fff',
    });

    return (
        <div>
            {profiles.length > 0 && (
                <div style={{ marginBottom: '8px' }}>
                    {/* header */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr auto', gap: '8px', padding: '4px 10px', fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>
                        <span>Platform</span><span>URL</span><span>Handle</span><span></span>
                    </div>
                    {profiles.map((p, i) => (
                        editingIdx === i ? (
                            <div key={i} style={{ ...rowStyle, background: '#eff6ff', border: '1px solid #93c5fd' }}>
                                <select value={draft.platform} onChange={e => setDraft(d => ({ ...d, platform: e.target.value }))} style={inputStyle}>
                                    {PROFILE_PLATFORMS.map(pl => <option key={pl} value={pl}>{pl}</option>)}
                                </select>
                                <input style={inputStyle} value={draft.url} onChange={e => setDraft(d => ({ ...d, url: e.target.value }))} placeholder="https://x.com/username" />
                                <input style={inputStyle} value={draft.handle} onChange={e => setDraft(d => ({ ...d, handle: e.target.value }))} placeholder="username (no @)" />
                                <div style={{ display: 'flex', gap: '4px' }}>
                                    <button style={btnStyle('#16a34a')} onClick={() => saveEdit(i)}>Save</button>
                                    <button style={btnStyle('#6b7280')} onClick={() => setEditingIdx(null)}>Cancel</button>
                                </div>
                            </div>
                        ) : (
                            <div key={i} style={rowStyle}>
                                <span style={{ fontSize: '13px', fontWeight: 600 }}>{PLATFORM_ICONS[p.platform] || '🌐'} {p.platform || '—'}</span>
                                <a href={p.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: '#2563eb', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{p.url || '—'}</a>
                                <span style={{ fontSize: '13px', color: '#374151' }}>{p.handle ? `@${p.handle}` : '—'}</span>
                                {!readOnly && (
                                    <div style={{ display: 'flex', gap: '4px' }}>
                                        <button style={btnStyle('#3b82f6')} onClick={() => startEdit(i)}>Edit</button>
                                        <button style={btnStyle('#ef4444')} onClick={() => deleteEntry(i)}>Del</button>
                                    </div>
                                )}
                            </div>
                        )
                    ))}
                </div>
            )}

            {adding ? (
                <div style={{ ...rowStyle, background: '#f0fdf4', border: '1px solid #86efac' }}>
                    <select value={draft.platform} onChange={e => setDraft(d => ({ ...d, platform: e.target.value }))} style={inputStyle}>
                        {PROFILE_PLATFORMS.map(pl => <option key={pl} value={pl}>{pl}</option>)}
                    </select>
                    <input style={inputStyle} value={draft.url} onChange={e => setDraft(d => ({ ...d, url: e.target.value }))} placeholder="https://x.com/username" autoFocus />
                    <input style={inputStyle} value={draft.handle} onChange={e => setDraft(d => ({ ...d, handle: e.target.value }))} placeholder="username (no @)" />
                    <div style={{ display: 'flex', gap: '4px' }}>
                        <button style={btnStyle('#16a34a')} onClick={confirmAdd}>Add</button>
                        <button style={btnStyle('#6b7280')} onClick={() => setAdding(false)}>Cancel</button>
                    </div>
                </div>
            ) : (
                !readOnly && (
                    <button onClick={startAdd} style={{ padding: '6px 14px', background: '#f3f4f6', border: '1px dashed #9ca3af', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', color: '#374151', fontWeight: 500 }}>
                        + Add Profile
                    </button>
                )
            )}

            {profiles.length === 0 && !adding && (
                <p style={{ fontSize: '12px', color: '#9ca3af', margin: '4px 0 8px' }}>No profiles added yet.</p>
            )}
        </div>
    );
}

// MultiSelectDialog Component for selecting multiple items from a collection (like Hosts or Authors)
function MultiSelectDialog({ isOpen, onClose, onConfirm, initialSelectedIds = [], collectionToFetch, title, navigate, location, createNewPath, onBeforeNavigate = null, fieldName }) {
    const [mode, setMode] = useState('existing'); // Default to existing to save a click
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set(initialSelectedIds.map(String)));

    const fetchItems = async () => {
        setLoading(true);
        try {
            const token = sessionStorage.getItem("token") || localStorage.getItem("token");
            const res = await fetch(API_ENDPOINTS.ADMIN.DATABASE_COLLECTION(collectionToFetch), {
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                },
                credentials: "include",
                cache: "no-store"
            });
            
            if (res.ok) {
                const json = await res.json();
                setItems(Array.isArray(json.data) ? json.data : []);
            }
        } catch (error) {
            console.error(`Error fetching ${collectionToFetch}:`, error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            // Re-initialize selection when dialog opens
            setSelectedIds(new Set(initialSelectedIds.map(String)));
            if (mode === 'existing') {
                fetchItems();
            }
        }
    }, [isOpen, mode, initialSelectedIds]); // eslint-disable-line react-hooks/exhaustive-deps

    // Check if returning from creating new item
    useEffect(() => {
        if (!isOpen) return;
        
        const urlParams = new URLSearchParams(location.search);
        const returnFromCreate = urlParams.get('returnFromCreate');
        const newId = urlParams.get('newId'); // Generic param
        const newGuestId = urlParams.get('newGuestId'); // Legacy param for podcast guests
        
        const createdId = newId || newGuestId;

        if (returnFromCreate === 'true' && createdId) {
            // Fetch the newly created item and add it
            const fetchAndAdd = async () => {
                try {
                    const token = sessionStorage.getItem("token") || localStorage.getItem("token");
                    const res = await fetch(API_ENDPOINTS.ADMIN.COLLECTION_GET(collectionToFetch, createdId), {
                        headers: {
                            "Content-Type": "application/json",
                            "Accept": "application/json",
                            ...(token ? { Authorization: `Bearer ${token}` } : {})
                        },
                        credentials: "include",
                        cache: "no-store"
                    });
                    
                    if (res.ok) {
                        const json = await res.json();
                        const item = json.data || json;
                        if (item) {
                            // Add the new ID to selected
                            const itemId = String(item._id || item.id);
                            const newSelected = new Set(selectedIds);
                            newSelected.add(itemId);
                            
                            // Automatically confirm and close
                            onConfirm(Array.from(newSelected));
                            onClose();
                            setMode('existing');
                        }
                    }
                } catch (error) {
                    console.error('Error fetching new item:', error);
                }
            };
            
            fetchAndAdd();
            // Clean up URL
            navigate(location.pathname, { replace: true });
        }
    }, [location.search, isOpen, navigate, onConfirm, collectionToFetch]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleToggleSelect = (id) => {
        const newSelected = new Set(selectedIds);
        if (newSelected.has(id)) {
            newSelected.delete(id);
        } else {
            newSelected.add(id);
        }
        setSelectedIds(newSelected);
    };

    const handleConfirmSelection = () => {
        onConfirm(Array.from(selectedIds));
        onClose();
        setMode('existing');
    };

    const handleAddNew = () => {
        // Save parent form state before navigating away
        if (onBeforeNavigate) onBeforeNavigate();

        // Store current location to return to
        // We append returnField to the return path so DynamicField knows which dialog to open
        const returnUrl = new URL(window.location.origin + location.pathname + location.search);
        returnUrl.searchParams.set('returnField', fieldName);
        const returnPath = returnUrl.pathname + returnUrl.search;
        localStorage.setItem('multiSelectReturnPath', returnPath);

        // Navigate to create page with return param
        // If createNewPath is provided, use it. Otherwise guess based on collection
        const path = createNewPath ? (createNewPath.includes('?') ? `${createNewPath}&returnToMultiSelect=true` : `${createNewPath}?returnToMultiSelect=true`) : `/admin/database/${collectionToFetch === 'PodcastGuests' ? 'podcastdb' : 'blogdb'}/${collectionToFetch}/create?returnToMultiSelect=true`;

        navigate(path);
    };

    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
        }} onClick={(e) => {
            if (e.target === e.currentTarget) {
                onClose();
                setMode('existing');
            }
        }}>
            <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '24px',
                maxWidth: '800px',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#0f172a' }}>{title}</h2>
                    <button
                        onClick={() => {
                            onClose();
                            setMode('existing');
                        }}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '24px',
                            cursor: 'pointer',
                            color: '#6b7280',
                            padding: '4px 8px',
                            lineHeight: 1
                        }}
                    >
                        ×
                    </button>
                </div>


                    <div>
                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
                        ) : items.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                                No items found.
                            </div>
                        ) : (
                            <>
                                <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '20px' }}>
                                    {items.map((item) => {
                                        const itemId = String(item._id || item.id);
                                        const isSelected = selectedIds.has(itemId);
                                        return (
                                            <div
                                                key={itemId}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '12px',
                                                    padding: '12px',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    marginBottom: '8px',
                                                    backgroundColor: isSelected ? '#eff6ff' : 'white',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s'
                                                }}
                                                onClick={() => handleToggleSelect(itemId)}
                                                onMouseEnter={(e) => {
                                                    if (!isSelected) {
                                                        e.currentTarget.style.backgroundColor = '#f9fafb';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!isSelected) {
                                                        e.currentTarget.style.backgroundColor = 'white';
                                                    }
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => handleToggleSelect(itemId)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{
                                                        width: '18px',
                                                        height: '18px',
                                                        cursor: 'pointer'
                                                    }}
                                                />
                                                {item.image && (
                                                    <img
                                                        src={typeof item.image === 'string' ? item.image : (item.image.url || '')}
                                                        alt={item.name || ''}
                                                        style={{
                                                            width: '60px',
                                                            height: '60px',
                                                            objectFit: 'cover',
                                                            borderRadius: '4px',
                                                            border: '1px solid #e5e7eb'
                                                        }}
                                                    />
                                                )}
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                                                        {item.name || item.title || 'Untitled'}
                                                    </div>
                                                    {item.email && (
                                                        <div style={{ fontSize: '14px', color: '#6b7280' }}>
                                                            {item.email}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div style={{ 
                                    display: 'flex', 
                                    gap: '12px', 
                                    justifyContent: 'space-between',
                                    marginTop: '20px',
                                    paddingTop: '20px',
                                    borderTop: '1px solid #f1f5f9'
                                }}>
                                    <button
                                        onClick={handleAddNew}
                                        style={{
                                            padding: '8px 16px',
                                            border: '1px solid #2563eb',
                                            borderRadius: '6px',
                                            backgroundColor: 'white',
                                            color: '#2563eb',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            transition: 'all 0.2s'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.backgroundColor = '#eff6ff';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.backgroundColor = 'white';
                                        }}
                                    >
                                        + Create New
                                    </button>

                                    <div style={{ display: 'flex', gap: '12px' }}>
                                        <button
                                            onClick={onClose}
                                            style={{
                                                padding: '8px 16px',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '6px',
                                                backgroundColor: 'white',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                fontWeight: 500
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleConfirmSelection}
                                            style={{
                                                padding: '8px 16px',
                                                border: 'none',
                                                borderRadius: '6px',
                                                backgroundColor: '#059669',
                                                color: 'white',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                fontWeight: 500
                                            }}
                                        >
                                            Update Selection ({selectedIds.size})
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
            </div>
        </div>
    );
}

// CouponCodeProductField Component - handles product selection with title display
function CouponCodeProductField({ value, onChange, hasError, renderFieldHeader, navigate, location }) {
    const [productDetails, setProductDetails] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [showProductSelector, setShowProductSelector] = useState(false);
    const [appliesToAllProducts, setAppliesToAllProducts] = useState(false);
    
    const currentProducts = Array.isArray(value) ? value : [];
    
    // Don't auto-detect "applies to all products" from empty array
    // Empty array means "no selection" for new documents
    // "Applies to All Products" is only set when user explicitly selects it
    // Reset appliesToAllProducts if products are added
    useEffect(() => {
        // If products are added, it's definitely not "all products"
        if (currentProducts.length > 0) {
            setAppliesToAllProducts(false);
        }
    }, [currentProducts.length]); // eslint-disable-line react-hooks/exhaustive-deps
    
    // Fetch product details for all product IDs
    useEffect(() => {
        const fetchProductDetails = async () => {
            // If applies to all products, don't fetch individual product details
            if (appliesToAllProducts || currentProducts.length === 0) {
                setProductDetails([]);
                return;
            }
            
            setLoadingProducts(true);
            try {
                const token = sessionStorage.getItem("token") || localStorage.getItem("token");
                const productPromises = currentProducts.map(async (productId) => {
                    try {
                        const res = await fetch(API_ENDPOINTS.ADMIN.COLLECTION_GET('ProductDetails', productId), {
                            headers: {
                                "Content-Type": "application/json",
                                "Accept": "application/json",
                                ...(token ? { Authorization: `Bearer ${token}` } : {})
                            },
                            credentials: "include",
                            cache: "no-store"
                        });
                        
                        if (res.ok) {
                            const json = await res.json();
                            return { id: productId, product: json.data || json };
                        } else {
                            return { id: productId, product: null };
                        }
                    } catch (error) {
                        console.error(`Error fetching product ${productId}:`, error);
                        return { id: productId, product: null };
                    }
                });
                
                const results = await Promise.all(productPromises);
                setProductDetails(results);
            } catch (error) {
                console.error('Error fetching product details:', error);
            } finally {
                setLoadingProducts(false);
            }
        };
        
        fetchProductDetails();
    }, [currentProducts.join(','), appliesToAllProducts]); // eslint-disable-line react-hooks/exhaustive-deps
    
    const handleSelectProducts = (productIds) => {
        // If productIds is empty array, check if it's from "Applies to All Products" selection
        if (productIds.length === 0) {
            // Check if appliesToAllProducts is true (set by handleSelectAllProducts)
            // If true, this is "Applies to All Products", otherwise it's just clearing
            if (appliesToAllProducts) {
                // User explicitly selected "Applies to All Products"
                onChange([]); // Empty array = all products
            }
            // If appliesToAllProducts is false and we get empty array, don't do anything
            // (This shouldn't happen, but handle gracefully)
        } else if (productIds.length > 0) {
            // User selected specific products
            const merged = appliesToAllProducts ? productIds : [...new Set([...currentProducts, ...productIds])];
            onChange(merged);
            setAppliesToAllProducts(false);
        }
    };
    
    const handleSelectAllProducts = (isAll) => {
        setAppliesToAllProducts(isAll);
        if (isAll) {
            // User explicitly selected "Applies to All Products"
            // Don't call onChange here - let handleSelectProducts do it
        } else {
            // User unselected "Applies to All Products", clear the selection
            onChange([]);
            setAppliesToAllProducts(false);
        }
    };
    
    const getProductTitle = (productId) => {
        const productDetail = productDetails.find(p => String(p.id) === String(productId));
        if (productDetail && productDetail.product) {
            return productDetail.product.title || String(productId);
        }
        return String(productId);
    };
    
    return (
        <div>
            {renderFieldHeader()}
                    <div style={{
                        border: `1px solid ${hasError ? '#dc2626' : '#d1d5db'}`,
                        borderRadius: '6px',
                        padding: '12px',
                        backgroundColor: hasError ? '#fef2f2' : 'white',
                        minHeight: '40px'
                    }}>
                        {appliesToAllProducts ? (
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '8px', 
                                marginBottom: '8px',
                                padding: '8px',
                                backgroundColor: '#d1fae5',
                                borderRadius: '4px',
                                fontSize: '14px',
                                color: '#059669',
                                fontWeight: 500
                            }}>
                                <span>✓ Applies to All Products</span>
                            </div>
                        ) : currentProducts.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                        {currentProducts.map((productId, index) => {
                            const productTitle = loadingProducts ? 'Loading...' : getProductTitle(productId);
                            return (
                                <div
                                    key={index}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '4px 8px',
                                        backgroundColor: '#f3f4f6',
                                        borderRadius: '4px',
                                        fontSize: '12px'
                                    }}
                                >
                                    <span>{productTitle}</span>
                                    <button
                                        onClick={() => {
                                            const updated = currentProducts.filter((_, i) => i !== index);
                                            onChange(updated);
                                        }}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#dc2626',
                                            cursor: 'pointer',
                                            padding: 0,
                                            fontSize: '14px',
                                            lineHeight: 1
                                        }}
                                    >
                                        ×
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                        ) : null}
                        {!appliesToAllProducts && currentProducts.length === 0 && (
                            <div style={{ color: '#6b7280', fontSize: '14px', marginBottom: '8px' }}>
                                No products selected
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={() => setShowProductSelector(true)}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: '#2563eb',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: 500
                            }}
                        >
                            {appliesToAllProducts ? 'Change Products' : (currentProducts.length > 0 ? 'Add More Products' : 'Add Products')}
                        </button>
            </div>
                    <ProductSelector
                        isOpen={showProductSelector}
                        onClose={() => setShowProductSelector(false)}
                        onSelectProducts={handleSelectProducts}
                        onSelectAllProducts={handleSelectAllProducts}
                        navigate={navigate}
                        location={location}
                    />
        </div>
    );
}

// ProductSelector Component for CouponCode product field
function ProductSelector({ isOpen, onClose, onSelectProducts, navigate, location, onSelectAllProducts }) {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState(new Set());
    const [searchTerm, setSearchTerm] = useState('');
    const [appliesToAll, setAppliesToAll] = useState(false);

    const fetchProducts = async () => {
        setLoading(true);
        try {
            // Check sessionStorage first (main source for admin session), then localStorage
            const token = sessionStorage.getItem("token") || localStorage.getItem("token");
            
            // Request more products (limit=1000) to ensure we get everything
            // Note: API_ENDPOINTS.ADMIN.DATABASE_COLLECTION returns URL like /admin/database?collection=ProductDetails
            const url = `${API_ENDPOINTS.ADMIN.DATABASE_COLLECTION('ProductDetails')}&limit=1000`;
            const res = await fetch(url, {
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                },
                credentials: "include",
                cache: "no-store"
            });
            
            if (res.ok) {
                const json = await res.json();
                setProducts(Array.isArray(json.data) ? json.data : []);
            }
        } catch (error) {
            console.error('Error fetching Products:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchProducts();
        }
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleToggleSelect = (productId) => {
        const newSelected = new Set(selectedProducts);
        if (newSelected.has(productId)) {
            newSelected.delete(productId);
        } else {
            newSelected.add(productId);
        }
        setSelectedProducts(newSelected);
    };

    const handleAddSelected = () => {
        if (appliesToAll) {
            // If "Applies to All Products" is selected, pass empty array to indicate all products
            // First notify that "all products" is selected, then pass empty array
            // Use setTimeout to ensure state is set before calling onSelectProducts
            onSelectAllProducts && onSelectAllProducts(true);
            setTimeout(() => {
                onSelectProducts([]);
            }, 0);
        } else {
            const selected = products.filter(product => selectedProducts.has(String(product._id || product.id)));
            if (selected.length > 0) {
                // Convert to array of IDs
                const productIds = selected.map(product => product._id || product.id);
                onSelectProducts(productIds);
            }
        }
        onClose();
        setSelectedProducts(new Set());
        setAppliesToAll(false);
    };

    const handleToggleAllProducts = () => {
        setAppliesToAll(!appliesToAll);
        setSelectedProducts(new Set()); // Clear individual selections when toggling all products
    };

    const filteredProducts = products.filter(product => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        const title = (product.title || '').toLowerCase();
        const category = (product.category || '').toLowerCase();
        const brand = (product.brand || '').toLowerCase();
        const productType = (product.productType || '').toLowerCase();
        return title.includes(search) || category.includes(search) || brand.includes(search) || productType.includes(search);
    });

    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
        }} onClick={(e) => {
            if (e.target === e.currentTarget) {
                onClose();
                setSelectedProducts(new Set());
            }
        }}>
            <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '24px',
                maxWidth: '900px',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#0f172a' }}>Select Products</h2>
                    <button
                        onClick={() => {
                            onClose();
                            setSelectedProducts(new Set());
                        }}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '24px',
                            cursor: 'pointer',
                            color: '#6b7280',
                            padding: '4px 8px',
                            lineHeight: 1
                        }}
                    >
                        ×
                    </button>
                </div>
                
                {/* Search Input */}
                <div style={{ marginBottom: '16px' }}>
                    <input
                        type="text"
                        placeholder="Search products by title, category, brand, or type..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px'
                        }}
                    />
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                        <i className="fi fi-br-spinner" style={{ animation: 'spin 1s linear infinite', fontSize: '24px', marginRight: '8px' }}></i>
                        Loading products...
                    </div>
                ) : filteredProducts.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                        {searchTerm ? 'No products found matching your search.' : 'No products available.'}
                    </div>
                ) : (
                    <>
                        <div style={{
                            maxHeight: '400px',
                            overflowY: 'auto',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            padding: '8px'
                        }}>
                            {filteredProducts.map((product) => {
                                const productId = String(product._id || product.id);
                                const isSelected = selectedProducts.has(productId);
                                return (
                                    <div
                                        key={productId}
                                        onClick={() => handleToggleSelect(productId)}
                                        style={{
                                            padding: '12px',
                                            marginBottom: '8px',
                                            border: `2px solid ${isSelected ? '#2563eb' : '#e5e7eb'}`,
                                            borderRadius: '6px',
                                            backgroundColor: isSelected ? '#eff6ff' : 'white',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => handleToggleSelect(productId)}
                                                onClick={(e) => e.stopPropagation()}
                                                style={{
                                                    width: '18px',
                                                    height: '18px',
                                                    cursor: 'pointer'
                                                }}
                                            />
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontWeight: 600, fontSize: '14px', color: '#111827', marginBottom: '4px' }}>
                                                    {product.title || 'Untitled Product'}
                                                </div>
                                                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                                    {product.category && <span>{product.category}</span>}
                                                    {product.brand && <span> • {product.brand}</span>}
                                                    {product.productType && <span> • {product.productType}</span>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginTop: '20px' }}>
                            <button
                                onClick={handleToggleAllProducts}
                                style={{
                                    padding: '8px 16px',
                                    border: `2px solid ${appliesToAll ? '#059669' : '#d1d5db'}`,
                                    borderRadius: '6px',
                                    backgroundColor: appliesToAll ? '#d1fae5' : 'white',
                                    color: appliesToAll ? '#059669' : '#374151',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={appliesToAll}
                                    onChange={handleToggleAllProducts}
                                    style={{
                                        width: '16px',
                                        height: '16px',
                                        cursor: 'pointer'
                                    }}
                                />
                                Applies to All Products
                            </button>
                            <div style={{ display: 'flex', gap: '12px' }}>
                                <button
                                    onClick={() => {
                                        onClose();
                                        setSelectedProducts(new Set());
                                        setAppliesToAll(false);
                                    }}
                                    style={{
                                        padding: '8px 16px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        backgroundColor: 'white',
                                        color: '#374151',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: 500
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleAddSelected}
                                    disabled={!appliesToAll && selectedProducts.size === 0}
                                    style={{
                                        padding: '8px 16px',
                                        border: 'none',
                                        borderRadius: '6px',
                                        backgroundColor: (appliesToAll || selectedProducts.size > 0) ? '#059669' : '#d1d5db',
                                        color: 'white',
                                        cursor: (appliesToAll || selectedProducts.size > 0) ? 'pointer' : 'not-allowed',
                                        fontSize: '14px',
                                        fontWeight: 500
                                    }}
                                >
                                    Add {appliesToAll ? '(All)' : `(${selectedProducts.size})`}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

// TranscriptField Component for PodcastsAudioDetails Transcript field
function TranscriptField({ value = [], onChange, label, documentId = null, onRefresh = null, onFileSelect = null }) {
    const [entries, setEntries] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadMessage, setUploadMessage] = useState('');

    useEffect(() => {
        // Initialize entries from value
        if (Array.isArray(value) && value.length > 0) {
            setEntries(value);
        } else {
            setEntries([]);
        }
    }, [value]);

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.name.endsWith('.json')) {
            alert('Please upload a JSON file');
            e.target.value = '';
            return;
        }

        if (!documentId) {
            // New logic: allow selection during creation
            if (onFileSelect) {
                onFileSelect(file);
                setUploadMessage(`📎 File "${file.name}" selected. It will be uploaded automatically after you save the document.`);
            } else {
                alert('Please save the document first before uploading transcript');
            }
            return;
        }

        setUploading(true);
        setUploadMessage('');

        try {
            const formData = new FormData();
            formData.append('file', file);

            const token = getAuthToken();
            // Construct the correct API endpoint
            // API_BASE_URL already includes '/api', so we just need '/admin/podcasts/...'
            const response = await fetch(`${API_BASE_URL}/admin/podcasts/${documentId}/upload-transcript`, {
                method: 'POST',
                headers: {
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                },
                body: formData,
                credentials: 'include'
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || 'Failed to upload transcript');
            }

            setUploadMessage(`✅ Transcript uploaded successfully! ${result.wordCount || 0} words processed.`);
            
            // Trigger refresh callback to refetch document data without page reload
            if (onRefresh && typeof onRefresh === 'function') {
                setTimeout(() => {
                    onRefresh();
                }, 500);
            }
        } catch (error) {
            setUploadMessage(`❌ Error: ${error.message}`);
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    return (
        <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: '#111827' }}>
                {label || 'Transcript'}
            </label>
            
            {/* File Upload Section */}
            <div style={{ 
                border: '1px solid #3b82f6', 
                borderRadius: '6px', 
                padding: '16px',
                backgroundColor: '#eff6ff',
                marginBottom: '16px'
            }}>
                <label style={{ 
                    display: 'block', 
                    fontSize: '13px', 
                    fontWeight: 600, 
                    marginBottom: '8px', 
                    color: '#1e40af' 
                }}>
                    Upload Transcript JSON File
                </label>
                {!documentId && !uploadMessage && (
                    <div style={{ 
                        marginBottom: '12px', 
                        padding: '8px 12px', 
                        backgroundColor: '#eff6ff', 
                        border: '1px solid #3b82f6', 
                        borderRadius: '4px',
                        fontSize: '13px',
                        color: '#1e40af'
                    }}>
                        <i className="fi fi-ss-info" style={{ marginRight: '6px' }}></i>
                        You can select a transcript file now. It will be uploaded as soon as you click "Save".
                    </div>
                )}
                <input
                    type="file"
                    accept=".json,application/json"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #3b82f6',
                        borderRadius: '4px',
                        fontSize: '14px',
                        backgroundColor: 'white',
                        cursor: uploading ? 'not-allowed' : 'pointer',
                        opacity: uploading ? 0.6 : 1
                    }}
                />
                {uploading && (
                    <div style={{ marginTop: '8px', color: '#1e40af', fontSize: '13px' }}>
                        <i className="fi fi-br-spinner" style={{ animation: 'spin 1s linear infinite', marginRight: '6px' }}></i>
                        Uploading and processing transcript...
                    </div>
                )}
                {uploadMessage && (
                    <div style={{ 
                        marginTop: '8px', 
                        fontSize: '13px',
                        color: uploadMessage.startsWith('✅') ? '#059669' : '#dc2626'
                    }}>
                        {uploadMessage}
                    </div>
                )}
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                    Expected format: JSON file with <code style={{ backgroundColor: '#e5e7eb', padding: '2px 4px', borderRadius: '3px' }}>{`{ chunks: [{ text: "...", timestamp: [start, end] }] }`}</code>
                </div>
            </div>
            
            {/* Display entries (read-only) */}
            {entries.length > 0 && (
                <div style={{ marginTop: '16px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', marginBottom: '8px' }}>
                        Transcript Entries ({entries.length}):
                    </div>
                    <div style={{ 
                        border: '1px solid #e5e7eb', 
                        borderRadius: '6px', 
                        padding: '12px',
                        backgroundColor: '#f9fafb',
                        maxHeight: '400px',
                        overflowY: 'auto'
                    }}>
                        {entries.map((entry, index) => {
                            // Display based on format (new format: word/start/end, old format: timestamp/text)
                            const displayText = entry.word || entry.text || `Entry ${index + 1}`;
                            const displayTime = entry.start !== undefined 
                                ? `${entry.start.toFixed(2)}s - ${entry.end.toFixed(2)}s`
                                : (entry.timestamp || 'N/A');
                            
                            return (
                                <div
                                    key={index}
                                    style={{
                                        padding: '8px',
                                        marginBottom: '4px',
                                        backgroundColor: 'white',
                                        borderRadius: '4px',
                                        fontSize: '13px',
                                        color: '#374151',
                                        border: '1px solid #e5e7eb'
                                    }}
                                >
                                    <span style={{ fontWeight: 600, color: '#059669', marginRight: '8px' }}>
                                        {displayTime}:
                                    </span>
                                    <span>{displayText}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

// AdPromotionsImageField Component for AdPromotions collection
function AdPromotionsImageField({ value = [], onChange, label }) {
    const [entries, setEntries] = useState([]);
    const [editingIndex, setEditingIndex] = useState(null);
    const [newImageUrl, setNewImageUrl] = useState('');
    const [newImageName, setNewImageName] = useState('');

    useEffect(() => {
        // Initialize entries from value
        if (Array.isArray(value) && value.length > 0) {
            setEntries(value.map(item => ({
                name: item.name || '',
                url: item.url || ''
            })));
        } else {
            setEntries([]);
        }
    }, [value]);

    const handleAdd = () => {
        if (newImageUrl.trim()) {
            const newEntry = {
                name: newImageName.trim() || '',
                url: newImageUrl.trim()
            };
            const updated = [...entries, newEntry];
            setEntries(updated);
            onChange(updated);
            setNewImageUrl('');
            setNewImageName('');
        }
    };

    const handleEdit = (index) => {
        setEditingIndex(index);
        setNewImageUrl(entries[index].url);
        setNewImageName(entries[index].name || '');
    };

    const handleSaveEdit = () => {
        if (editingIndex !== null && newImageUrl.trim()) {
            const updated = [...entries];
            updated[editingIndex] = {
                name: newImageName.trim() || '',
                url: newImageUrl.trim()
            };
            setEntries(updated);
            onChange(updated);
            setEditingIndex(null);
            setNewImageUrl('');
            setNewImageName('');
        }
    };

    const handleCancelEdit = () => {
        setEditingIndex(null);
        setNewImageUrl('');
        setNewImageName('');
    };

    const handleDelete = (index) => {
        const updated = entries.filter((_, i) => i !== index);
        setEntries(updated);
        onChange(updated);
    };

    return (
        <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: '#111827' }}>
                {label || 'Image'}
            </label>
            
            {/* Existing entries */}
            {entries.length > 0 && (
                <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {entries.map((entry, index) => (
                        <div
                            key={index}
                            style={{
                                border: '1px solid #e5e7eb',
                                borderRadius: '6px',
                                padding: '12px',
                                backgroundColor: '#f9fafb',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px'
                            }}
                        >
                            {entry.url && (
                                <img
                                    src={entry.url}
                                    alt={entry.name || 'Image'}
                                    style={{
                                        width: '60px',
                                        height: '60px',
                                        objectFit: 'cover',
                                        borderRadius: '4px',
                                        border: '1px solid #d1d5db'
                                    }}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                                {entry.name && (
                                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>
                                        {entry.name}
                                    </div>
                                )}
                                <div style={{ fontSize: '12px', color: '#6b7280', wordBreak: 'break-all' }}>
                                    {entry.url}
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                    type="button"
                                    onClick={() => handleEdit(index)}
                                    style={{
                                        padding: '6px 12px',
                                        backgroundColor: '#2563eb',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: 500
                                    }}
                                >
                                    Edit
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleDelete(index)}
                                    style={{
                                        padding: '6px 12px',
                                        backgroundColor: '#dc2626',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: 500
                                    }}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Add/Edit form */}
            <div style={{
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                padding: '16px',
                backgroundColor: editingIndex !== null ? '#fef3c7' : 'white'
            }}>
                {editingIndex !== null && (
                    <div style={{ marginBottom: '12px', fontSize: '13px', color: '#92400e', fontWeight: 500 }}>
                        Editing image {editingIndex + 1}
                    </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px', color: '#374151' }}>
                            Image Name (Optional)
                        </label>
                        <input
                            type="text"
                            value={newImageName}
                            onChange={(e) => setNewImageName(e.target.value)}
                            placeholder="Enter image name..."
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}
                        />
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px', color: '#374151' }}>
                            Image URL <span style={{ color: '#dc2626' }}>*</span>
                        </label>
                        <input
                            type="url"
                            value={newImageUrl}
                            onChange={(e) => setNewImageUrl(e.target.value)}
                            placeholder="https://example.com/image.jpg"
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    if (editingIndex !== null) {
                                        handleSaveEdit();
                                    } else {
                                        handleAdd();
                                    }
                                }
                            }}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        {editingIndex !== null ? (
                            <>
                                <button
                                    type="button"
                                    onClick={handleSaveEdit}
                                    disabled={!newImageUrl.trim()}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: '#059669',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: newImageUrl.trim() ? 'pointer' : 'not-allowed',
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        opacity: newImageUrl.trim() ? 1 : 0.6
                                    }}
                                >
                                    Save
                                </button>
                                <button
                                    type="button"
                                    onClick={handleCancelEdit}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: '#6b7280',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: 500
                                    }}
                                >
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                onClick={handleAdd}
                                disabled={!newImageUrl.trim()}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#2563eb',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: newImageUrl.trim() ? 'pointer' : 'not-allowed',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    opacity: newImageUrl.trim() ? 1 : 0.6
                                }}
                            >
                                Add Image
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ImagesField Component for ProductDetails collection
function ImagesField({ value = [], onChange, label }) {
    const [entries, setEntries] = useState([]);
    const [editingIndex, setEditingIndex] = useState(null);
    const [newImageUrl, setNewImageUrl] = useState('');
    const [newAltText, setNewAltText] = useState('');

    useEffect(() => {
        // Initialize entries from value
        if (Array.isArray(value) && value.length > 0) {
            setEntries(value.map(item => ({
                url: item.url || '',
                altText: item.altText || ''
            })));
        } else {
            setEntries([]);
        }
    }, [value]);

    const handleAdd = () => {
        if (newImageUrl.trim()) {
            const newEntry = {
                url: newImageUrl.trim(),
                altText: newAltText.trim()
            };
            const updated = [...entries, newEntry];
            setEntries(updated);
            onChange(updated);
            setNewImageUrl('');
            setNewAltText('');
        }
    };

    const handleEdit = (index) => {
        setEditingIndex(index);
        setNewImageUrl(entries[index].url);
        setNewAltText(entries[index].altText);
    };

    const handleSaveEdit = () => {
        if (editingIndex !== null && newImageUrl.trim()) {
            const updated = [...entries];
            updated[editingIndex] = {
                url: newImageUrl.trim(),
                altText: newAltText.trim()
            };
            setEntries(updated);
            onChange(updated);
            setEditingIndex(null);
            setNewImageUrl('');
            setNewAltText('');
        }
    };

    const handleCancelEdit = () => {
        setEditingIndex(null);
        setNewImageUrl('');
        setNewAltText('');
    };

    const handleDelete = (index) => {
        const updated = entries.filter((_, i) => i !== index);
        setEntries(updated);
        onChange(updated);
    };

    return (
        <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: '#111827' }}>
                {label || 'Images'}
            </label>
            
            {/* Existing entries */}
            {entries.length > 0 && (
                <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {entries.map((entry, index) => (
                        <div
                            key={index}
                            style={{
                                border: '1px solid #e5e7eb',
                                borderRadius: '6px',
                                padding: '12px',
                                backgroundColor: '#f9fafb',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px'
                            }}
                        >
                            {entry.url && (
                                <img
                                    src={entry.url}
                                    alt={entry.altText || 'Image'}
                                    style={{
                                        width: '60px',
                                        height: '60px',
                                        objectFit: 'cover',
                                        borderRadius: '4px',
                                        border: '1px solid #d1d5db'
                                    }}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                                    URL: {entry.url || 'N/A'}
                                </div>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                    Alt Text: {entry.altText || 'N/A'}
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                    type="button"
                                    onClick={() => handleEdit(index)}
                                    style={{
                                        padding: '6px 12px',
                                        backgroundColor: '#3b82f6',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: 500
                                    }}
                                >
                                    Edit
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleDelete(index)}
                                    style={{
                                        padding: '6px 12px',
                                        backgroundColor: '#dc2626',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: 500
                                    }}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Add/Edit form */}
            <div style={{
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                padding: '16px',
                backgroundColor: 'white'
            }}>
                <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#6b7280' }}>
                        Image URL
                    </label>
                    <input
                        type="text"
                        value={newImageUrl}
                        onChange={(e) => setNewImageUrl(e.target.value)}
                        placeholder="https://example.com/image.jpg"
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px'
                        }}
                    />
                </div>
                <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#6b7280' }}>
                        Alt Text
                    </label>
                    <input
                        type="text"
                        value={newAltText}
                        onChange={(e) => setNewAltText(e.target.value)}
                        placeholder="Descriptive alt text"
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px'
                        }}
                    />
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                    {editingIndex !== null ? (
                        <>
                            <button
                                type="button"
                                onClick={handleSaveEdit}
                                disabled={!newImageUrl.trim()}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: newImageUrl.trim() ? '#059669' : '#9ca3af',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: newImageUrl.trim() ? 'pointer' : 'not-allowed',
                                    fontSize: '14px',
                                    fontWeight: 500
                                }}
                            >
                                Save
                            </button>
                            <button
                                type="button"
                                onClick={handleCancelEdit}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#6b7280',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 500
                                }}
                            >
                                Cancel
                            </button>
                        </>
                    ) : (
                        <button
                            type="button"
                            onClick={handleAdd}
                            disabled={!newImageUrl.trim()}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: newImageUrl.trim() ? '#2563eb' : '#9ca3af',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: newImageUrl.trim() ? 'pointer' : 'not-allowed',
                                fontSize: '14px',
                                fontWeight: 500
                            }}
                        >
                            Add Image
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

// VariantsField Component for ProductDetails collection
function VariantsField({ value = [], onChange, label }) {
    const [entries, setEntries] = useState([]);
    const [editingIndex, setEditingIndex] = useState(null);
    const [newHeading, setNewHeading] = useState('');
    const [newValue, setNewValue] = useState('');
    const [isArray, setIsArray] = useState(false);

    useEffect(() => {
        // Initialize entries from value
        if (Array.isArray(value) && value.length > 0) {
            setEntries(value.map(item => {
                // Handle different formats
                if (item.heading && item.value !== undefined) {
                    return {
                        heading: item.heading || '',
                        value: item.value,
                        isArray: Array.isArray(item.value)
                    };
                } else if (item.type && item.options) {
                    // Legacy format: {type: "Size", options: ["S", "M", "L"]}
                    return {
                        heading: item.type || '',
                        value: item.options || [],
                        isArray: true
                    };
                } else {
                    // Generic object
                    return {
                        heading: Object.keys(item)[0] || '',
                        value: Object.values(item)[0] || '',
                        isArray: Array.isArray(Object.values(item)[0])
                    };
                }
            }));
        } else {
            setEntries([]);
        }
    }, [value]);

    const handleAdd = () => {
        if (newHeading.trim()) {
            const newEntry = {
                heading: newHeading.trim(),
                value: isArray ? (newValue.trim() ? newValue.split(',').map(v => v.trim()).filter(v => v) : []) : newValue.trim(),
                isArray: isArray
            };
            const updated = [...entries, newEntry];
            setEntries(updated);
            onChange(updated.map(e => ({
                heading: e.heading,
                value: e.value
            })));
            setNewHeading('');
            setNewValue('');
            setIsArray(false);
        }
    };

    const handleEdit = (index) => {
        setEditingIndex(index);
        setNewHeading(entries[index].heading);
        if (Array.isArray(entries[index].value)) {
            setNewValue(entries[index].value.join(', '));
            setIsArray(true);
        } else {
            setNewValue(String(entries[index].value || ''));
            setIsArray(false);
        }
    };

    const handleSaveEdit = () => {
        if (editingIndex !== null && newHeading.trim()) {
            const updated = [...entries];
            updated[editingIndex] = {
                heading: newHeading.trim(),
                value: isArray ? (newValue.trim() ? newValue.split(',').map(v => v.trim()).filter(v => v) : []) : newValue.trim(),
                isArray: isArray
            };
            setEntries(updated);
            onChange(updated.map(e => ({
                heading: e.heading,
                value: e.value
            })));
            setEditingIndex(null);
            setNewHeading('');
            setNewValue('');
            setIsArray(false);
        }
    };

    const handleCancelEdit = () => {
        setEditingIndex(null);
        setNewHeading('');
        setNewValue('');
        setIsArray(false);
    };

    const handleDelete = (index) => {
        const updated = entries.filter((_, i) => i !== index);
        setEntries(updated);
        onChange(updated.map(e => ({
            heading: e.heading,
            value: e.value
        })));
    };

    return (
        <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: '#111827' }}>
                {label || 'Variants'}
            </label>
            
            {/* Existing entries */}
            {entries.length > 0 && (
                <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {entries.map((entry, index) => (
                        <div
                            key={index}
                            style={{
                                border: '1px solid #e5e7eb',
                                borderRadius: '6px',
                                padding: '12px',
                                backgroundColor: '#f9fafb'
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                                        Heading: {entry.heading || 'N/A'}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                        Value: {Array.isArray(entry.value) ? entry.value.join(', ') : String(entry.value || 'N/A')}
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                                        Type: {entry.isArray ? 'Array' : 'String'}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button
                                        type="button"
                                        onClick={() => handleEdit(index)}
                                        style={{
                                            padding: '6px 12px',
                                            backgroundColor: '#3b82f6',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '12px',
                                            fontWeight: 500
                                        }}
                                    >
                                        Edit
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleDelete(index)}
                                        style={{
                                            padding: '6px 12px',
                                            backgroundColor: '#dc2626',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '12px',
                                            fontWeight: 500
                                        }}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Add/Edit form */}
            <div style={{
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                padding: '16px',
                backgroundColor: 'white'
            }}>
                <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#6b7280' }}>
                        Heading
                    </label>
                    <input
                        type="text"
                        value={newHeading}
                        onChange={(e) => setNewHeading(e.target.value)}
                        placeholder="e.g., Size, Color, Material"
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px'
                        }}
                    />
                </div>
                <div style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={isArray}
                                onChange={(e) => setIsArray(e.target.checked)}
                                style={{
                                    width: '18px',
                                    height: '18px',
                                    cursor: 'pointer'
                                }}
                            />
                            <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>
                                Value is an array (comma-separated)
                            </span>
                        </label>
                    </div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#6b7280' }}>
                        {isArray ? 'Values (comma-separated)' : 'Value'}
                    </label>
                    <input
                        type="text"
                        value={newValue}
                        onChange={(e) => setNewValue(e.target.value)}
                        placeholder={isArray ? "e.g., S, M, L, XL" : "e.g., Red"}
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px'
                        }}
                    />
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                    {editingIndex !== null ? (
                        <>
                            <button
                                type="button"
                                onClick={handleSaveEdit}
                                disabled={!newHeading.trim()}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: newHeading.trim() ? '#059669' : '#9ca3af',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: newHeading.trim() ? 'pointer' : 'not-allowed',
                                    fontSize: '14px',
                                    fontWeight: 500
                                }}
                            >
                                Save
                            </button>
                            <button
                                type="button"
                                onClick={handleCancelEdit}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#6b7280',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 500
                                }}
                            >
                                Cancel
                            </button>
                        </>
                    ) : (
                        <button
                            type="button"
                            onClick={handleAdd}
                            disabled={!newHeading.trim()}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: newHeading.trim() ? '#2563eb' : '#9ca3af',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: newHeading.trim() ? 'pointer' : 'not-allowed',
                                fontSize: '14px',
                                fontWeight: 500
                            }}
                        >
                            Add Variant
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

// DiscountByCountryField Component
function DiscountByCountryField({ value = [], onChange, renderFieldHeader, fieldName }) {
    const [entries, setEntries] = useState([]);
    const [editingIndex, setEditingIndex] = useState(null);
    const [newRule, setNewRule] = useState({ countryCode: 'IN', currency: 'INR', amount: '' });

    useEffect(() => {
        if (Array.isArray(value)) {
            setEntries(value);
        } else {
            setEntries([]);
        }
    }, [value]);

    const handleAdd = () => {
        if (newRule.amount && !isNaN(Number(newRule.amount)) && newRule.countryCode && newRule.currency) {
            const updated = [...entries, { ...newRule, amount: Number(newRule.amount) }];
            onChange(updated);
            setNewRule({ countryCode: 'IN', currency: 'INR', amount: '' });
        }
    };

    const handleEdit = (index) => {
        setEditingIndex(index);
        setNewRule({
            countryCode: entries[index].countryCode,
            currency: entries[index].currency,
            amount: String(entries[index].amount)
        });
    };

    const handleSaveEdit = () => {
        if (editingIndex !== null && newRule.amount && !isNaN(Number(newRule.amount)) && newRule.countryCode && newRule.currency) {
            const updated = [...entries];
            updated[editingIndex] = { ...newRule, amount: Number(newRule.amount) };
            onChange(updated);
            setEditingIndex(null);
            setNewRule({ countryCode: 'IN', currency: 'INR', amount: '' });
        }
    };

    const handleCancelEdit = () => {
        setEditingIndex(null);
        setNewRule({ countryCode: 'IN', currency: 'INR', amount: '' });
    };

    const handleRemove = (index) => {
        const updated = entries.filter((_, i) => i !== index);
        onChange(updated);
        if (editingIndex === index) {
            handleCancelEdit();
        } else if (editingIndex > index) {
            setEditingIndex(editingIndex - 1);
        }
    };

    const isMinimumOrderValue = fieldName === 'minimumOrderValueByCountry';

    return (
        <div>
           {renderFieldHeader()}
           <div style={{ border: '1px solid #d1d5db', borderRadius: '6px', padding: '12px', backgroundColor: '#f9fafb' }}>
               {/* List entries */}
               {entries.length > 0 && (
                   <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                       <div style={{ 
                           display: 'grid', 
                           gridTemplateColumns: 'minmax(80px, 1fr) minmax(80px, 1fr) minmax(100px, 1.5fr) auto', 
                           gap: '12px', 
                           fontSize: '12px', 
                           fontWeight: 600, 
                           color: '#6b7280', 
                           padding: '0 8px' 
                       }}>
                           <div>Country</div>
                           <div>Currency</div>
                           <div>{isMinimumOrderValue ? 'Min Order' : 'Amount'}</div>
                           <div style={{ textAlign: 'right' }}>Actions</div>
                       </div>
                       {entries.map((entry, idx) => (
                           <div key={idx} style={{ 
                               display: 'grid', 
                               gridTemplateColumns: 'minmax(80px, 1fr) minmax(80px, 1fr) minmax(100px, 1.5fr) auto', 
                               gap: '12px', 
                               alignItems: 'center',
                               backgroundColor: editingIndex === idx ? '#eff6ff' : 'white',
                               padding: '10px 12px',
                               borderRadius: '6px',
                               border: `1px solid ${editingIndex === idx ? '#3b82f6' : '#e5e7eb'}`,
                               boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                           }}>
                               <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>{entry.countryCode}</div>
                               <div style={{ fontSize: '14px', color: '#4b5563' }}>{entry.currency}</div>
                               <div style={{ fontSize: '14px', fontWeight: 700, color: '#059669' }}>{entry.amount}</div>
                               <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                   <button 
                                       onClick={() => handleEdit(idx)}
                                       style={{
                                           padding: '4px 8px',
                                           backgroundColor: '#f3f4f6',
                                           color: '#2563eb',
                                           border: '1px solid #d1d5db',
                                           borderRadius: '4px',
                                           cursor: 'pointer',
                                           fontSize: '12px',
                                           fontWeight: 500
                                       }}
                                   >
                                       Edit
                                   </button>
                                   <button 
                                       onClick={() => handleRemove(idx)}
                                       style={{
                                           padding: '4px 8px',
                                           backgroundColor: '#fee2e2',
                                           color: '#dc2626',
                                           border: '1px solid #fecaca',
                                           borderRadius: '4px',
                                           cursor: 'pointer',
                                           fontSize: '12px',
                                           fontWeight: 500
                                       }}
                                   >
                                       Remove
                                   </button>
                               </div>
                           </div>
                       ))}
                   </div>
               )}
               
               {/* Add/Edit Section */}
               <div style={{ 
                   backgroundColor: 'white', 
                   padding: '16px', 
                   borderRadius: '8px', 
                   border: '1px solid #e5e7eb',
                   marginTop: entries.length > 0 ? '12px' : '0'
               }}>
                   <div style={{ 
                       fontSize: '13px', 
                       fontWeight: 700, 
                       marginBottom: '12px', 
                       color: '#374151',
                       display: 'flex',
                       alignItems: 'center',
                       gap: '6px'
                   }}>
                       <i className={`fi ${editingIndex !== null ? 'fi-rr-edit' : 'fi-rr-add-document'}`} style={{ color: '#2563eb' }}></i>
                       {editingIndex !== null ? `Editing Rule #${editingIndex + 1}` : 'Add New Country Rule'}
                   </div>
                   
                   <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#6b7280', marginBottom: '4px' }}>Country Code</label>
                            <input 
                                value={newRule.countryCode} 
                                onChange={e => setNewRule({...newRule, countryCode: e.target.value.toUpperCase()})} 
                                placeholder="USA, IN" 
                                style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }} 
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#6b7280', marginBottom: '4px' }}>Currency</label>
                            <input 
                                value={newRule.currency} 
                                onChange={e => setNewRule({...newRule, currency: e.target.value.toUpperCase()})} 
                                placeholder="USD, INR" 
                                style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }} 
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#6b7280', marginBottom: '4px' }}>{isMinimumOrderValue ? 'Min Value' : 'Discount'}</label>
                            <input 
                                type="number" 
                                value={newRule.amount} 
                                onChange={e => setNewRule({...newRule, amount: e.target.value})} 
                                placeholder="Amount" 
                                style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }} 
                            />
                        </div>
                   </div>
                   
                   <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        {editingIndex !== null && (
                            <button 
                                onClick={handleCancelEdit}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: 'white',
                                    color: '#374151',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontWeight: 600,
                                    fontSize: '14px'
                                }}
                            >
                                Cancel
                            </button>
                        )}
                        <button 
                            onClick={editingIndex !== null ? handleSaveEdit : handleAdd}
                            disabled={!newRule.amount || !newRule.countryCode || !newRule.currency}
                            style={{
                                padding: '10px 24px',
                                backgroundColor: (!newRule.amount || !newRule.countryCode || !newRule.currency) ? '#9ca3af' : (editingIndex !== null ? '#059669' : '#2563eb'),
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: (!newRule.amount || !newRule.countryCode || !newRule.currency) ? 'not-allowed' : 'pointer',
                                fontWeight: 700,
                                fontSize: '14px',
                                transition: 'all 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            {editingIndex !== null ? (
                                <><i className="fi fi-rr-check"></i> Save Changes</>
                            ) : (
                                <><i className="fi fi-rr-plus"></i> Add Rule</>
                            )}
                        </button>
                   </div>
               </div>
           </div>
        </div>
    );
}

// Combined Country and Currency Field for CouponCode
function CountryCurrencyCombinedField({ countries = [], currencies = [], onChange, renderFieldHeader, readOnly }) {
    // Combine into rows
    // Special 'DEFAULT' country code for Rest of World
    const rows = (Array.isArray(countries) ? countries : []).map((c, i) => ({
        country: c,
        currency: (Array.isArray(currencies) ? currencies[i] : '') || ''
    }));

    const regularRows = rows.filter(r => r.country !== 'DEFAULT');
    const defaultRow = rows.find(r => r.country === 'DEFAULT') || { country: 'DEFAULT', currency: 'USD' };
    
    // Check if DEFAULT is in the countries array
    const isDefaultEnabled = (Array.isArray(countries) ? countries : []).includes('DEFAULT');

    const triggerChange = (updatedRegularRows, defaultEnabled, defaultCurrency) => {
        const finalRows = [...updatedRegularRows];
        if (defaultEnabled) {
            finalRows.push({ country: 'DEFAULT', currency: defaultCurrency });
        }
        
        const newCountries = finalRows.map(r => r.country);
        const newCurrencies = finalRows.map(r => r.currency);
        onChange(newCountries, newCurrencies);
    };

    const handleRowChange = (index, field, value) => {
        const newRegularRows = [...regularRows];
        newRegularRows[index] = { ...newRegularRows[index], [field]: value.toUpperCase() };
        triggerChange(newRegularRows, isDefaultEnabled, defaultRow.currency);
    };

    const handleAddRow = () => {
        const newRegularRows = [...regularRows, { country: '', currency: '' }];
        triggerChange(newRegularRows, isDefaultEnabled, defaultRow.currency);
    };

    const handleRemoveRow = (index) => {
        const newRegularRows = regularRows.filter((_, i) => i !== index);
        triggerChange(newRegularRows, isDefaultEnabled, defaultRow.currency);
    };

    const handleDefaultToggle = () => {
        triggerChange(regularRows, !isDefaultEnabled, defaultRow.currency);
    };

    const handleDefaultCurrencyChange = (val) => {
        triggerChange(regularRows, isDefaultEnabled, val.toUpperCase());
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
            {renderFieldHeader()}
            
            <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '12px', 
                border: '1px solid #e5e7eb', 
                padding: '16px', 
                borderRadius: '10px', 
                backgroundColor: '#f9fafb',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)'
            }}>
                {regularRows.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '12px', color: '#6b7280', fontSize: '13px', fontStyle: 'italic' }}>
                        No specific countries added.
                    </div>
                )}
                
                {regularRows.map((row, idx) => (
                    <div key={idx} style={{ 
                        display: 'flex', 
                        gap: '12px', 
                        alignItems: 'center',
                        backgroundColor: 'white',
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                    }}>
                        <div style={{ flex: 1 }}>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#6b7280', marginBottom: '4px' }}>Country Code</label>
                            <input 
                                placeholder="e.g. IN, USA" 
                                value={row.country} 
                                onChange={e => handleRowChange(idx, 'country', e.target.value)}
                                readOnly={readOnly}
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#6b7280', marginBottom: '4px' }}>Currency</label>
                            <input 
                                placeholder="e.g. INR, USD" 
                                value={row.currency} 
                                onChange={e => handleRowChange(idx, 'currency', e.target.value)}
                                readOnly={readOnly}
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                            />
                        </div>
                        {!readOnly && (
                            <button 
                                onClick={() => handleRemoveRow(idx)} 
                                style={{ 
                                    alignSelf: 'flex-end',
                                    marginBottom: '4px',
                                    padding: '8px',
                                    background: '#fef2f2', 
                                    border: '1px solid #fee2e2', 
                                    color: '#ef4444', 
                                    cursor: 'pointer',
                                    borderRadius: '6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fee2e2'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fef2f2'; }}
                            >
                                <i className="fi fi-rr-trash"></i>
                            </button>
                        )}
                    </div>
                ))}
                
                {!readOnly && (
                    <button 
                        onClick={handleAddRow} 
                        style={{ 
                            alignSelf: 'flex-start', 
                            padding: '8px 16px', 
                            fontSize: '13px', 
                            fontWeight: 600,
                            backgroundColor: '#3b82f6', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '6px', 
                            cursor: 'pointer', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)',
                            transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#2563eb'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#3b82f6'; }}
                    >
                        <i className="fi fi-rr-plus"></i> Add Country Entry
                    </button>
                )}
            </div>

            <div style={{ 
                padding: '16px', 
                border: `1px solid ${isDefaultEnabled ? '#3b82f6' : '#e5e7eb'}`, 
                borderRadius: '10px', 
                backgroundColor: isDefaultEnabled ? '#eff6ff' : '#f9fafb', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '16px',
                transition: 'all 0.3s ease'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }} onClick={!readOnly ? handleDefaultToggle : undefined}>
                    <input 
                        type="checkbox" 
                        checked={isDefaultEnabled} 
                        onChange={() => {}} // Handled by parent div click
                        disabled={readOnly}
                        style={{ width: '18px', height: '18px', cursor: readOnly ? 'not-allowed' : 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', fontWeight: 600, color: isDefaultEnabled ? '#1e40af' : '#374151' }}>Rest of the Countries</span>
                </div>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end' }}>
                    <span style={{ fontSize: '13px', color: '#6b7280' }}>Default Currency:</span>
                    <input 
                        placeholder="Currency" 
                        value={defaultRow.currency} 
                        onChange={e => handleDefaultCurrencyChange(e.target.value)}
                        readOnly={readOnly || !isDefaultEnabled}
                        style={{ 
                            width: '90px', 
                            padding: '8px 10px', 
                            border: '1px solid #d1d5db', 
                            borderRadius: '6px', 
                            fontSize: '14px',
                            backgroundColor: (readOnly || !isDefaultEnabled) ? '#f3f4f6' : 'white',
                            color: isDefaultEnabled ? '#111827' : '#9ca3af',
                            fontWeight: 600
                        }}
                    />
                </div>
            </div>
        </div>
    );
}

// Custom Component for "What's in the Box" in ProductDetails
function WhatsInTheBoxField({ value, onChange, hasError, renderFieldHeader, readOnly = false }: any) {
    let parsedData = { enabled: false, items: [] };
    if (value && typeof value === 'object') {
        parsedData = {
            enabled: Boolean(value.enabled),
            items: Array.isArray(value.items) ? value.items : []
        };
    } else if (typeof value === 'string' && value.trim()) {
        try {
            const parsed = JSON.parse(value);
            if (typeof parsed === 'object' && parsed !== null) {
                parsedData = {
                    enabled: Boolean(parsed.enabled),
                    items: Array.isArray(parsed.items) ? parsed.items : []
                };
            }
        } catch (e) {
            // Ignore parse error
        }
    }

    const isEnabled = parsedData.enabled;
    const items = parsedData.items;

    const handleToggle = () => {
        if (readOnly) return;
        const nextEnabled = !isEnabled;
        onChange({
            enabled: nextEnabled,
            items: items.length === 0 && nextEnabled
                ? [{ imageUrl: '', title: '', subtitle: '' }]
                : items
        });
    };

    const handleAddItem = () => {
        if (readOnly) return;
        onChange({
            enabled: true,
            items: [...items, { imageUrl: '', title: '', subtitle: '' }]
        });
    };

    const handleRemoveItem = (index) => {
        if (readOnly) return;
        const updated = items.filter((_, i) => i !== index);
        onChange({
            enabled: isEnabled,
            items: updated
        });
    };

    const handleItemChange = (index, field, val) => {
        if (readOnly) return;
        const updated = items.map((item, i) => {
            if (i === index) {
                return { ...item, [field]: val };
            }
            return item;
        });
        onChange({
            enabled: isEnabled,
            items: updated
        });
    };

    return (
        <div style={{
            border: `1px solid ${hasError ? '#dc2626' : '#e2e8f0'}`,
            borderRadius: '12px',
            padding: '16px',
            backgroundColor: '#ffffff',
            marginBottom: '16px'
        }}>
            {renderFieldHeader()}

            {/* Toggle header row */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 16px',
                borderRadius: '8px',
                backgroundColor: isEnabled ? '#f0fdf4' : '#f8fafc',
                border: `1px solid ${isEnabled ? '#86efac' : '#e2e8f0'}`,
                marginBottom: isEnabled ? '16px' : '0'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input
                        type="checkbox"
                        id="whatsInTheBoxToggle"
                        checked={isEnabled}
                        onChange={handleToggle}
                        disabled={readOnly}
                        style={{ width: '18px', height: '18px', cursor: readOnly ? 'not-allowed' : 'pointer' }}
                    />
                    <label
                        htmlFor="whatsInTheBoxToggle"
                        style={{
                            fontWeight: 600,
                            fontSize: '14px',
                            color: isEnabled ? '#15803d' : '#475569',
                            cursor: readOnly ? 'not-allowed' : 'pointer',
                            margin: 0
                        }}
                    >
                        Enable "What's in the Box" Section on Product Page
                    </label>
                </div>
                <span style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    padding: '3px 10px',
                    borderRadius: '9999px',
                    backgroundColor: isEnabled ? '#dcfce7' : '#f1f5f9',
                    color: isEnabled ? '#166534' : '#64748b'
                }}>
                    {isEnabled ? 'Enabled' : 'Optional (Disabled)'}
                </span>
            </div>

            {/* Items list when enabled */}
            {isEnabled && (
                <div>
                    <p style={{ fontSize: '13px', color: '#64748b', marginTop: 12, marginBottom: '14px' }}>
                        Add images and labels for items bundled inside the box (e.g. device, charger, cable, stickers). These display in a horizontal scrolling gallery with hover enlargement.
                    </p>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {items.map((item, idx) => (
                            <div
                                key={idx}
                                style={{
                                    display: 'flex',
                                    gap: '12px',
                                    alignItems: 'center',
                                    padding: '12px',
                                    borderRadius: '8px',
                                    border: '1px solid #e2e8f0',
                                    backgroundColor: '#f8fafc'
                                }}
                            >
                                <span style={{
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    color: '#64748b',
                                    padding: '6px 8px',
                                    backgroundColor: '#e2e8f0',
                                    borderRadius: '6px',
                                    minWidth: '28px',
                                    textAlign: 'center'
                                }}>
                                    #{idx + 1}
                                </span>

                                {/* Thumbnail preview */}
                                <div style={{
                                    width: '56px',
                                    height: '56px',
                                    borderRadius: '6px',
                                    border: '1px solid #cbd5e1',
                                    backgroundColor: '#ffffff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                    flexShrink: 0
                                }}>
                                    {item.imageUrl ? (
                                        <img
                                            src={item.imageUrl}
                                            alt={item.title || 'Item preview'}
                                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                        />
                                    ) : (
                                        <i className="fi fi-rr-picture" style={{ color: '#94a3b8', fontSize: '20px' }}></i>
                                    )}
                                </div>

                                {/* Inputs grid */}
                                <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: '8px' }}>
                                    <div>
                                        <label style={{ fontSize: '11px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '2px' }}>
                                            Image URL *
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="https://... image URL"
                                            value={item.imageUrl || ''}
                                            onChange={(e) => handleItemChange(idx, 'imageUrl', e.target.value)}
                                            readOnly={readOnly}
                                            style={{
                                                width: '100%',
                                                padding: '6px 10px',
                                                border: '1px solid #cbd5e1',
                                                borderRadius: '6px',
                                                fontSize: '13px',
                                                backgroundColor: readOnly ? '#f1f5f9' : '#ffffff',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '11px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '2px' }}>
                                            Item Name / Title *
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="e.g. iPhone 3G or iPhone 3GS"
                                            value={item.title || ''}
                                            onChange={(e) => handleItemChange(idx, 'title', e.target.value)}
                                            readOnly={readOnly}
                                            style={{
                                                width: '100%',
                                                padding: '6px 10px',
                                                border: '1px solid #cbd5e1',
                                                borderRadius: '6px',
                                                fontSize: '13px',
                                                backgroundColor: readOnly ? '#f1f5f9' : '#ffffff',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '11px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '2px' }}>
                                            Subtitle / Description (Optional)
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="e.g. Second-hand vintage device"
                                            value={item.subtitle || ''}
                                            onChange={(e) => handleItemChange(idx, 'subtitle', e.target.value)}
                                            readOnly={readOnly}
                                            style={{
                                                width: '100%',
                                                padding: '6px 10px',
                                                border: '1px solid #cbd5e1',
                                                borderRadius: '6px',
                                                fontSize: '13px',
                                                backgroundColor: readOnly ? '#f1f5f9' : '#ffffff',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                </div>

                                {!readOnly && (
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveItem(idx)}
                                        style={{
                                            border: 'none',
                                            background: 'transparent',
                                            color: '#ef4444',
                                            cursor: 'pointer',
                                            padding: '8px',
                                            borderRadius: '6px',
                                            fontSize: '16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                        title="Remove item"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>

                    {!readOnly && (
                        <button
                            type="button"
                            onClick={handleAddItem}
                            style={{
                                marginTop: '14px',
                                padding: '8px 16px',
                                borderRadius: '8px',
                                border: '1px solid #2563eb',
                                backgroundColor: '#eff6ff',
                                color: '#2563eb',
                                fontSize: '13px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                transition: 'all 0.15s ease'
                            }}
                        >
                            + Add Item to Box
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

// Main DynamicField Component
export default function DynamicField({
    label,
    value,
    onChange,
    fieldType,
    fieldName,
    hasError = false,
    placeholder,
    collectionName,
    authorOptions = [],
    categoryOptions = [],
    postTopicOptions = [],
    productOptions = [],
    isRequired = false,
    documentId = null,
    onRefresh = null,
    readOnly = false,
    allData = {},
    onUpdateField = null,
    onBeforeNavigate = null,
    onTranscriptFileSelect = null
}) {
    const navigate = useNavigate();
    const location = useLocation();
    const [showHostsDialog, setShowHostsDialog] = useState(false);
    const type = getType(value);
    const [jsonError, setJsonError] = useState(null);
    
    // Generic handler for returning from creating a new item in a related collection
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const returnFromCreate = urlParams.get('returnFromCreate');
        const returnField = urlParams.get('returnField');
        const newId = urlParams.get('newId');
        const newGuestId = urlParams.get('newGuestId');
        
        // Open the dialog if this field is the one we're returning to
        // Either explicitly via 'returnField' or fallback for legacy 'hosts' field
        if (returnFromCreate === 'true' && (newId || newGuestId)) {
            if (fieldName === returnField || (!returnField && fieldName === 'hosts' && collectionName === 'PodcastsAudioDetails')) {
                setShowHostsDialog(true);
                // Note: MultiSelectDialog will handle cleaning up the URL after it fetches the new item
            }
        }
    }, [location.search, fieldName, collectionName]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleClear = (e) => {
        // Prevent blur event from firing when clicking Clear button
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        
        // Clear the value based on field type
        if (fieldType === 'boolean') {
            onChange(false);
        } else if (fieldType === 'number') {
            onChange('');
        } else if (fieldType === 'array') {
            onChange([]);
        } else if (fieldType === 'object') {
            onChange({});
        } else {
            onChange('');
        }
    };

    const renderFieldHeader = () => {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '8px' 
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {label && (
                        <label 
                            htmlFor={fieldName}
                            style={{ 
                                display: 'block', 
                                fontSize: '14px', 
                                fontWeight: 600,
                                color: '#111827'
                            }}
                        >
                        {label}
                    </label>
                )}
                    {isRequired && (
                        <span style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            backgroundColor: '#f3f4f6',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 500,
                            color: '#6b7280'
                        }}>
                            Required
                        </span>
                    )}
                </div>
                <span
                    role="button"
                    tabIndex={-1}
                    aria-hidden="true"
                    onClick={(e) => handleClear(e)}
                    onMouseDown={(e) => {
                        // Prevent input blur when clicking Clear button
                        e.preventDefault();
                    }}
                    style={{
                        padding: '4px 12px',
                        backgroundColor: '#f3f4f6',
                        border: 'none',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 500,
                        color: '#6b7280',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                        outline: 'none',
                        userSelect: 'none',
                        display: 'inline-block'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#e5e7eb';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#f3f4f6';
                    }}
                >
                    Clear
                </span>
            </div>
        );
    };

    // Special handling for CouponCode product field - check FIRST before any other type checks
    // This must come first because fieldType is 'productarray', and value might be null/undefined
    if (collectionName === 'CouponCode' && fieldName === 'product') {
        // Ensure value is always an array for CouponCodeProductField
        const productValue = Array.isArray(value) ? value : [];
        return (
            <CouponCodeProductField
                value={productValue}
                onChange={onChange}
                hasError={hasError}
                renderFieldHeader={renderFieldHeader}
                navigate={navigate}
                location={location}
            />
        );
    }

    // Handle "What's in the Box" field for ProductDetails
    if (fieldName === 'whatsInTheBox') {
        return (
            <WhatsInTheBoxField
                value={value}
                onChange={onChange}
                hasError={hasError}
                renderFieldHeader={renderFieldHeader}
                readOnly={readOnly}
            />
        );
    }

    // Explicitly handle audioFile to ensure it works as a text input
    if (fieldName === 'audioFile') {
        return (
            <div>
                {renderFieldHeader()}
                <input
                    type="text"
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="Enter audio file URL (mp3)"
                    style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: `1px solid ${hasError ? '#dc2626' : '#d1d5db'}`,
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: hasError ? '#fef2f2' : 'white'
                    }}
                />
            </div>
        );
    }

    // Handle date fields
    if (fieldType === 'date') {
        // Safely parse date value, handling invalid dates
        let dateValue = '';
        if (value && value !== '') {
            try {
                const date = new Date(value);
                // Check if date is valid
                if (!isNaN(date.getTime())) {
                    // unexpected behavior with toISOString().split('T')[0] as it converts to UTC
                    // We want local time for datetime-local input
                    const pad = (num) => num.toString().padStart(2, '0');
                    const year = date.getFullYear();
                    const month = pad(date.getMonth() + 1);
                    const day = pad(date.getDate());
                    const hours = pad(date.getHours());
                    const minutes = pad(date.getMinutes());
                    dateValue = `${year}-${month}-${day}T${hours}:${minutes}`;
                }
            } catch (e) {
                // If parsing fails, leave dateValue as empty string
                dateValue = '';
            }
        }
        
        return (
            <div>
                {renderFieldHeader()}
                <input
                    type="datetime-local"
                    value={dateValue}
                    onChange={(e) => {
                        if (e.target.value) {
                            try {
                                const date = new Date(e.target.value);
                                if (!isNaN(date.getTime())) {
                                    onChange(date.toISOString());
                                } else {
                                    onChange('');
                                }
                            } catch (err) {
                                onChange('');
                            }
                        } else {
                            onChange('');
                        }
                    }}
                    style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: `1px solid ${hasError ? '#dc2626' : '#d1d5db'}`,
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: hasError ? '#fef2f2' : 'white',
                        cursor: 'pointer',
                        position: 'relative'
                    }}
                    onClick={(e) => {
                        // Ensure date picker opens on click
                        if (e.target.showPicker) {
                            e.target.showPicker();
                        }
                    }}
                />
            </div>
        );
    }

    // Handle enum fields
    if (fieldType === 'enum') {
        let enumOptions = [];
        if (fieldName === 'role') {
            enumOptions = ['user', 'staff', 'admin'];
        } else if (fieldName === 'orderStatus') {
            enumOptions = ['placed', 'not placed'];
        } else if (fieldName === 'guestType') {
            enumOptions = ['staff', 'guest'];
        } else if (fieldName === 'productType') {
            enumOptions = ['physical', 'digital', 'used'];
        } else if (fieldName === 'paymentMode') {
            enumOptions = ['LOCAL', 'USD_ONLY'];
        }
        
        if (enumOptions.length > 0) {
            return (
                <div>
                    {renderFieldHeader()}
                    <select
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '8px 36px 8px 12px',
                            border: `1px solid ${hasError ? '#dc2626' : '#d1d5db'}`,
                            borderRadius: '6px',
                            fontSize: '14px',
                            backgroundColor: hasError ? '#fef2f2' : 'white',
                            appearance: 'none',
                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23374151' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 12px center',
                            backgroundSize: '12px',
                            cursor: 'pointer'
                        }}
                    >
                        <option value="">Select {label || fieldName}</option>
                        {enumOptions.map(option => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                </div>
            );
        }
    }

    // Handle objectid fields with dropdowns for BlogContent, PostTopics, and ProductPrices collections
    // Check this BEFORE null check so dropdowns work even when value is null
    if (fieldType === 'objectid') {
        // Special handling for Author, Category, and PostTopic in BlogContent
        // Special handling for whichCategory in PostTopics
        // Special handling for productId in ProductPrices
        if (collectionName === 'BlogContent' || collectionName === 'PostTopics' || (collectionName === 'ProductPrices' && fieldName === 'productId')) {
            let options = [];
            let displayKey = 'name';
            
            if (collectionName === 'BlogContent') {
                if (fieldName === 'author') {
                    options = authorOptions;
                    displayKey = 'name';
                } else if (fieldName === 'category') {
                    options = categoryOptions;
                    displayKey = 'name';
                } else if (fieldName === 'postTopic') {
                    options = postTopicOptions;
                    displayKey = 'topic';
                }
            } else if (collectionName === 'PostTopics') {
                if (fieldName === 'whichCategory') {
                    options = categoryOptions;
                    displayKey = 'name';
                }
            } else if (collectionName === 'ProductPrices' && fieldName === 'productId') {
                options = productOptions;
                displayKey = 'title';
            }
            
            if (options.length > 0) {
                // Ensure options are sorted ascending
                const sortedOptions = [...options].sort((a, b) => {
                    const labelA = a[displayKey] || a.name || a.topic || a.title || '';
                    const labelB = b[displayKey] || b.name || b.topic || b.title || '';
                    return String(labelA).localeCompare(String(labelB));
                });

                // Get current selected value (could be ObjectId string or object with _id)
                const currentValueId = value && typeof value === 'object' && value._id 
                    ? String(value._id) 
                    : (value ? String(value) : '');
                
                return (
                    <div>
                        {renderFieldHeader()}
                        <select
                            value={currentValueId}
                            onChange={(e) => {
                                const selectedId = e.target.value;
                                if (selectedId) {
                                    // Save as ObjectId string (backend will handle conversion)
                                    onChange(selectedId);
                                } else {
                                    onChange('');
                                }
                            }}
                            style={{
                                width: '100%',
                                padding: '8px 36px 8px 12px',
                                border: `1px solid ${hasError ? '#dc2626' : '#d1d5db'}`,
                                borderRadius: '6px',
                                fontSize: '14px',
                                backgroundColor: hasError ? '#fef2f2' : 'white',
                                appearance: 'none',
                                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23374151' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'right 12px center',
                                backgroundSize: '12px',
                                cursor: 'pointer'
                            }}
                        >
                            <option value="">Select {label || fieldName}</option>
                            {sortedOptions.map(option => (
                                <option key={String(option._id)} value={String(option._id)}>
                                    {option[displayKey] || option.name || option.topic || String(option._id)}
                                </option>
                            ))}
                        </select>
                        {currentValueId && (() => {
                            const selectedOption = sortedOptions.find(opt => String(opt._id) === currentValueId);
                            const displayValue = selectedOption && selectedOption[displayKey] ? selectedOption[displayKey] : currentValueId;
                            return (
                                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                                    Selected: {displayValue}
                                </div>
                            );
                        })()}
                    </div>
                );
            }
        }
        
        // Fallback to text input for other objectid fields
        return (
            <div>
                {renderFieldHeader()}
                <input
                    type="text"
                    value={value ? String(value) : ''}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder || `Enter ObjectId`}
                    style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: `1px solid ${hasError ? '#dc2626' : '#d1d5db'}`,
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: hasError ? '#fef2f2' : 'white',
                        fontFamily: 'monospace'
                    }}
                />
            </div>
        );
    }

    // Handle object fields (including empty/null objects)
    // Check this BEFORE null check so dropdowns work even when value is null
    if (fieldType === 'object') {
        const objValue = (value && typeof value === 'object' && value !== null) ? value : {};

        // Special case: category, postTopic in BlogContent (these are populated objects)
        if (collectionName === 'BlogContent' && (fieldName === 'category' || fieldName === 'postTopic')) {
            let options = [];
            let displayKey = 'name';
            
            if (fieldName === 'category') {
                options = categoryOptions;
                displayKey = 'name';
            } else if (fieldName === 'postTopic') {
                options = postTopicOptions;
                displayKey = 'topic';
            }
            
            if (options.length > 0) {
                // Ensure options are sorted ascending
                const sortedOptions = [...options].sort((a, b) => {
                    const labelA = a[displayKey] || a.name || a.topic || a.title || '';
                    const labelB = b[displayKey] || b.name || b.topic || b.title || '';
                    return String(labelA).localeCompare(String(labelB));
                });

                // Get current selected value (could be ObjectId string, object with _id, or populated object)
                const currentValueId = value && typeof value === 'object' && value._id 
                    ? String(value._id) 
                    : (value ? String(value) : '');
                
                return (
                    <div>
                        {renderFieldHeader()}
                        <select
                            value={currentValueId}
                            onChange={(e) => {
                                const selectedId = e.target.value;
                                if (selectedId) {
                                    // Save as ObjectId string (backend will handle conversion)
                                    onChange(selectedId);
                                } else {
                                    onChange('');
                                }
                            }}
                            style={{
                                width: '100%',
                                padding: '8px 36px 8px 12px',
                                border: `1px solid ${hasError ? '#dc2626' : '#d1d5db'}`,
                                borderRadius: '6px',
                                fontSize: '14px',
                                backgroundColor: hasError ? '#fef2f2' : 'white',
                                appearance: 'none',
                                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23374151' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'right 12px center',
                                backgroundSize: '12px',
                                cursor: 'pointer'
                            }}
                        >
                            <option value="">Select {label || fieldName}</option>
                            {sortedOptions.map(option => (
                                <option key={String(option._id)} value={String(option._id)}>
                                    {option[displayKey] || option.name || option.topic || String(option._id)}
                                </option>
                            ))}
                        </select>
                        {currentValueId && (() => {
                            const selectedOption = sortedOptions.find(opt => String(opt._id) === currentValueId);
                            const displayValue = selectedOption && selectedOption[displayKey] ? selectedOption[displayKey] : currentValueId;
                            return (
                                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                                    Selected: {displayValue}
                                </div>
                            );
                        })()}
                    </div>
                );
            }
        }

        // Special case: coverImage or image fields with url and fileName (or imageTitle)
        // Check for coverImage field, image field in AuthorDetails, or any object with image-like properties
        // Priority: field name check first (works even with empty object), then check object properties
        const isImageField = fieldName === 'coverImage' || 
                            (collectionName === 'AuthorDetails' && fieldName === 'image') ||
                            (objValue && (objValue.url || objValue.fileName || objValue.imageTitle));
        
        if (isImageField) {
            return (
                <div>
                    {renderFieldHeader()}
                    <ImagePreviewField
                        value={objValue}
                        onChange={onChange}
                        label={label || fieldName}
                        onClear={handleClear}
                    />
                </div>
            );
        }

        // Special case: shippingAddress or billingAddress
        if (fieldName === 'shippingAddress' || fieldName === 'billingAddress') {
            return (
                <div>
                    {renderFieldHeader()}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#6b7280' }}>Name</label>
                            <input
                                type="text"
                                value={objValue.name || ''}
                                onChange={(e) => onChange({ ...objValue, name: e.target.value })}
                                placeholder="Full name"
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#6b7280' }}>Street</label>
                            <input
                                type="text"
                                value={objValue.street || ''}
                                onChange={(e) => onChange({ ...objValue, street: e.target.value })}
                                placeholder="Street address"
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#6b7280' }}>City</label>
                            <input
                                type="text"
                                value={objValue.city || ''}
                                onChange={(e) => onChange({ ...objValue, city: e.target.value })}
                                placeholder="City"
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#6b7280' }}>State</label>
                            <input
                                type="text"
                                value={objValue.state || ''}
                                onChange={(e) => onChange({ ...objValue, state: e.target.value })}
                                placeholder="State"
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#6b7280' }}>ZIP</label>
                            <input
                                type="text"
                                value={objValue.zip || ''}
                                onChange={(e) => onChange({ ...objValue, zip: e.target.value })}
                                placeholder="ZIP code"
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#6b7280' }}>Country</label>
                            <input
                                type="text"
                                value={objValue.country || ''}
                                onChange={(e) => onChange({ ...objValue, country: e.target.value })}
                                placeholder="Country"
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                            />
                        </div>
                    </div>
                </div>
            );
        }

        // Generic JSON editor for other objects
        const objString = typeof objValue === 'object' && objValue !== null ? JSON.stringify(objValue, null, 2) : '';
        return (
            <div>
                {renderFieldHeader()}
                <textarea
                    value={objString}
                    onChange={(e) => {
                        try {
                            if (e.target.value.trim() === '') {
                                setJsonError(null);
                                onChange({});
                            } else {
                                const parsed = JSON.parse(e.target.value);
                                if (typeof parsed === 'object' && !Array.isArray(parsed)) {
                                    setJsonError(null);
                                    onChange(parsed);
                                } else {
                                    setJsonError('Value must be an object, not an array or primitive');
                                }
                            }
                        } catch (err) {
                            const trimmed = e.target.value.trim();
                            if (trimmed === '' || trimmed === '{' || trimmed.startsWith('{')) {
                                setJsonError(null);
                            } else {
                                setJsonError(`Invalid JSON: ${err.message}`);
                            }
                        }
                    }}
                    rows={8}
                    style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: `1px solid ${jsonError || hasError ? '#dc2626' : '#d1d5db'}`,
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontFamily: 'monospace',
                        backgroundColor: jsonError || hasError ? '#fef2f2' : 'white',
                        lineHeight: '1.5',
                        resize: 'none'
                    }}
                    placeholder='Enter JSON object, e.g., {"key": "value"}'
                />
                {jsonError && (
                    <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                        {jsonError}
                    </div>
                )}
                {!jsonError && objValue && typeof objValue === 'object' && Object.keys(objValue).length > 0 && (
                    <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                        Object with {Object.keys(objValue).length} key{Object.keys(objValue).length !== 1 ? 's' : ''}
                    </div>
                )}
            </div>
        );
    }

    // Special handling for discountType in CouponCode - use PERCENT/FIXED radio buttons
    // This must be before the boolean check since discountType is an enum, not boolean
    if (collectionName === 'CouponCode' && fieldName === 'discountType') {
        const currentValue = value || 'PERCENT';
        return (
            <div>
                {renderFieldHeader()}
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                            type="radio"
                            name={fieldName}
                            value="PERCENT"
                            checked={currentValue === 'PERCENT'}
                            onChange={() => onChange('PERCENT')}
                            style={{
                                width: '16px',
                                height: '16px',
                                cursor: 'pointer'
                            }}
                        />
                        <span style={{ fontSize: '14px' }}>Percentage (%)</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                            type="radio"
                            name={fieldName}
                            value="FIXED"
                            checked={currentValue === 'FIXED'}
                            onChange={() => onChange('FIXED')}
                            style={{
                                width: '16px',
                                height: '16px',
                                cursor: 'pointer'
                            }}
                        />
                        <span style={{ fontSize: '14px' }}>Fixed Amount (by Country)</span>
                    </label>
                </div>
                {hasError && (
                    <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                        This field is required
                    </div>
                )}
            </div>
        );
    }

    // Handle discountByCountry and minimumOrderValueByCountry for CouponCode
    if (collectionName === 'CouponCode' && (fieldName === 'discountByCountry' || fieldName === 'minimumOrderValueByCountry')) {
        return <DiscountByCountryField value={value} onChange={onChange} renderFieldHeader={renderFieldHeader} fieldName={fieldName} />;
    }

    // Handle booleans - check BEFORE null/string checks so radio buttons work even when value is null
    if (fieldType === 'boolean') {
        // Special handling for BlogContent boolean fields (isPublished, priorityPost, noIndex)
        if (collectionName === 'BlogContent' && (fieldName === 'isPublished' || fieldName === 'priorityPost' || fieldName === 'noIndex')) {
            const boolValue = value === true || value === 'true';
    return (
        <div>
                    {renderFieldHeader()}
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <input
                                type="radio"
                                name={fieldName}
                                value="yes"
                                checked={boolValue === true}
                                onChange={() => onChange(true)}
                                style={{
                                    width: '16px',
                                    height: '16px',
                                    cursor: 'pointer'
                                }}
                            />
                            <span style={{ fontSize: '14px' }}>Yes</span>
                </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <input
                                type="radio"
                                name={fieldName}
                                value="no"
                                checked={boolValue === false}
                                onChange={() => onChange(false)}
                                style={{
                                    width: '16px',
                                    height: '16px',
                                    cursor: 'pointer'
                                }}
                            />
                            <span style={{ fontSize: '14px' }}>No</span>
                        </label>
                    </div>
                    {hasError && (
                        <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                            This field is required
                        </div>
                    )}
                </div>
            );
        }
        
        // Default dropdown for other boolean fields
        const boolValue = value === true || value === 'true';
        return (
            <div>
                {renderFieldHeader()}
                <select
                    value={boolValue ? 'true' : 'false'}
                    onChange={(e) => onChange(e.target.value === 'true')}
                    style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: `1px solid ${hasError ? '#dc2626' : '#d1d5db'}`,
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: hasError ? '#fef2f2' : 'white'
                    }}
                >
                    <option value="false">false</option>
                    <option value="true">true</option>
                </select>
            </div>
        );
    }

    // Handle numbers (Check this BEFORE null/undefined check so we render number input even if value is null)
    if (type === 'number' || fieldType === 'number') {
        return (
            <div>
                {renderFieldHeader()}
                <input
                    type="number"
                    value={value !== null && value !== undefined ? value : ''}
                    onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
                    placeholder={placeholder || `Enter ${label || fieldName}`}
                    style={{
                        width: (fieldName === 'postNumber' || fieldName === 'podcastNumber' || fieldName === 'authorNumber' || fieldName === 'guestNumber') ? '150px' : '100%',
                        padding: '8px 12px',
                        border: `1px solid ${hasError ? '#dc2626' : '#d1d5db'}`,
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: hasError ? '#fef2f2' : 'white'
                    }}
                />
            </div>
        );
    }
    
    // Handle null/undefined (only if not handled by fieldType checks above)
    if (type === 'null') {
        return (
            <div>
                {renderFieldHeader()}
                <input
                    type="text"
                    value=""
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder || `Enter ${label || fieldName}`}
                    style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: `1px solid ${hasError ? '#dc2626' : '#d1d5db'}`,
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: hasError ? '#fef2f2' : 'white'
                    }}
                />
            </div>
        );
    }

    // Special handling for PodcastsAudioDetails hosts field OR BlogContent author field (now array)
    // Placed before generic string handler to correctly handle legacy string data (single ID) by normalizing it to an array
    if ((collectionName === 'PodcastsAudioDetails' && fieldName === 'hosts') || 
        (collectionName === 'BlogContent' && fieldName === 'author')) {
        
        const isHosts = fieldName === 'hosts';
        const collectionToFetch = isHosts ? 'PodcastGuests' : 'AuthorDetails';
        const itemTitle = isHosts ? 'Hosts' : 'Authors';
        const createNewPath = isHosts 
            ? `/admin/database/podcastdb/PodcastGuests/create` 
            : `/admin/database/blogdb/AuthorDetails/create`;
        
        const handleUpdateSelection = (ids) => {
            onChange(ids);
        };
        
        // Helper to normalize values to array of IDs (handling legacy single strings or populated objects)
        const normalizeToIds = (val) => {
            if (!val) return [];
            if (Array.isArray(val)) {
                return val.map(item => {
                    if (typeof item === 'object' && item !== null) {
                        return item._id || item.id || String(item);
                    }
                    return String(item);
                });
            }
            if (typeof val === 'object' && val !== null) {
                return [val._id || val.id || String(val)];
            }
            return [String(val)];
        };

        const currentIds = normalizeToIds(value);
        
        return (
            <div>
                {renderFieldHeader()}
                <div style={{
                    border: `1px solid ${hasError ? '#dc2626' : '#d1d5db'}`,
                    borderRadius: '6px',
                    padding: '12px',
                    backgroundColor: hasError ? '#fef2f2' : 'white',
                    minHeight: '40px'
                }}>
                    {currentIds.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                            {currentIds.map((id, index) => (
                                <div
                                    key={index}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '4px 8px',
                                        backgroundColor: '#f3f4f6',
                                        borderRadius: '4px',
                                        fontSize: '12px'
                                    }}
                                >
                                    <span>{String(id)}</span>
                                    <button
                                        onClick={() => {
                                            const updated = currentIds.filter((_, i) => i !== index);
                                            onChange(updated);
                                        }}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#dc2626',
                                            cursor: 'pointer',
                                            padding: 0,
                                            fontSize: '14px',
                                            lineHeight: 1
                                        }}
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{ color: '#6b7280', fontSize: '14px', marginBottom: '8px' }}>
                            No {itemTitle.toLowerCase()} selected
                        </div>
                    )}
                    <button
                        type="button"
                        onClick={() => setShowHostsDialog(true)}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#2563eb',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 500
                        }}
                    >
                        {currentIds.length > 0 ? `Edit ${itemTitle}` : `Add ${itemTitle}`}
                    </button>
                </div>
                <MultiSelectDialog
                    isOpen={showHostsDialog}
                    onClose={() => setShowHostsDialog(false)}
                    onConfirm={handleUpdateSelection}
                    initialSelectedIds={currentIds}
                    collectionToFetch={collectionToFetch}
                    title={`Select ${itemTitle}`}
                    navigate={navigate}
                    location={location}
                    createNewPath={createNewPath}
                    onBeforeNavigate={onBeforeNavigate}
                    fieldName={fieldName}
                />
            </div>
        );
    }

    // Handle strings
    if (type === 'string' || (fieldType === 'string' && type !== 'object')) {
        const isImageUrlField = collectionName === 'ImageAssets' && fieldName === 'url';
        const isVideoAssetUrl = isImageUrlField && typeof value === 'string' && /\.(mp4|webm|ogg|mov|m4v|m3u8)(\?|#|$)/i.test(value);
        return (
            <div>
                {renderFieldHeader()}
                {(fieldName.toLowerCase().includes('bio') || fieldName.toLowerCase() === 'overview') ? (
                    <textarea
                        id={fieldName}
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={placeholder || `Enter ${label || fieldName}`}
                        tabIndex={0}
                        style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: `1px solid ${hasError ? '#dc2626' : '#d1d5db'}`,
                            borderRadius: '6px',
                            fontSize: '14px',
                            lineHeight: '1.6',
                            backgroundColor: hasError ? '#fef2f2' : 'white',
                            resize: 'none',
                            fontFamily: 'inherit',
                            height: '110px',
                            minHeight: '110px',
                            maxHeight: '110px',
                            overflowY: 'auto'
                        }}
                    />
                ) : (
                    <input
                        id={fieldName}
                        type="text"
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={placeholder || `Enter ${label || fieldName}`}
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: `1px solid ${hasError ? '#dc2626' : '#d1d5db'}`,
                            borderRadius: '6px',
                            fontSize: '14px',
                            backgroundColor: hasError ? '#fef2f2' : 'white',
                            height: 'auto'
                        }}
                    />
                )}
                {isImageUrlField && value && (
                    <div style={{ marginTop: '10px' }}>
                        {isVideoAssetUrl ? (
                            <video
                                src={value}
                                style={{
                                    maxWidth: '300px',
                                    maxHeight: '200px',
                                    borderRadius: '6px',
                                    border: '1px solid #d1d5db',
                                    objectFit: 'contain',
                                    display: 'block',
                                    backgroundColor: '#111827'
                                }}
                                controls
                                preload="metadata"
                            />
                        ) : (
                            <img
                                src={value}
                                alt="Preview"
                                style={{
                                    maxWidth: '300px',
                                    maxHeight: '200px',
                                    borderRadius: '6px',
                                    border: '1px solid #d1d5db',
                                    objectFit: 'contain',
                                    display: 'block'
                                }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                                onLoad={(e) => { e.target.style.display = 'block'; }}
                            />
                        )}
                    </div>
                )}
            </div>
        );
    }




    // Handle arrays - use TagInput for simple string arrays, JSON editor for complex arrays
    // Check fieldType first to handle null/undefined values correctly
    if (fieldType === 'array' || fieldType === 'productarray' || type === 'array') {
        const arrayValue = Array.isArray(value) ? value : [];
        
        // Special handling for PodcastsAudioDetails transcript field
        if (collectionName === 'PodcastsAudioDetails' && fieldName === 'transcript') {
            return (
                <div>
                    {renderFieldHeader()}
                    <TranscriptField
                        value={arrayValue}
                        onChange={onChange}
                        label={label}
                        documentId={documentId}
                        onRefresh={onRefresh}
                        onFileSelect={onTranscriptFileSelect}
                    />
                </div>
            );
        }
        

        
        // Special handling for AdPromotions image field
        if (collectionName === 'AdPromotions' && fieldName === 'image') {
            return (
                <div>
                    {renderFieldHeader()}
                    <AdPromotionsImageField
                        value={arrayValue}
                        onChange={onChange}
                        label={label}
                    />
                </div>
            );
        }
        
        // Special handling for ProductDetails images field
        if (collectionName === 'ProductDetails' && fieldName === 'images') {
            return (
                <div>
                    {renderFieldHeader()}
                    <ImagesField
                        value={arrayValue}
                        onChange={onChange}
                        label={label}
                    />
                </div>
            );
        }

        // Special handling for AffiliateProductDetails productImages field
        if (collectionName === 'AffiliateProductDetails' && fieldName === 'productImages') {
            return (
                <div>
                    {renderFieldHeader()}
                    <ImagesField
                        value={arrayValue}
                        onChange={onChange}
                        label={label}
                    />
                </div>
            );
        }
        
        // Special handling for ProductDetails displayImage field
        if (collectionName === 'ProductDetails' && fieldName === 'displayImage') {
            return (
                <div>
                    {renderFieldHeader()}
                    <ImagesField
                        value={arrayValue}
                        onChange={onChange}
                        label={label}
                    />
                </div>
            );
        }
        
        // Special handling for ProductDetails variants field
        if (collectionName === 'ProductDetails' && fieldName === 'variants') {
            return (
                <div>
                    {renderFieldHeader()}
                    <VariantsField
                        value={arrayValue}
                        onChange={onChange}
                        label={label}
                    />
                </div>
            );
        }
        
        // Special handling for ProductDetails tags field
        if (collectionName === 'ProductDetails' && fieldName === 'tags') {
            return (
                <div>
                    {renderFieldHeader()}
                    <TagInput
                        value={arrayValue}
                        onChange={(newArray) => {
                            // Ensure we always pass an array, never null/undefined
                            onChange(Array.isArray(newArray) ? newArray : []);
                        }}
                        placeholder="Add tags separated by commas and press Enter"
                    />
                </div>
            );
        }

        // Special handling for AffiliateProductDetails productType field
        if (collectionName === 'AffiliateProductDetails' && fieldName === 'productType') {
            return (
                <div>
                    {renderFieldHeader()}
                    <TagInput
                        value={arrayValue}
                        onChange={(newArray) => {
                            // Ensure we always pass an array, never null/undefined
                            onChange(Array.isArray(newArray) ? newArray : []);
                        }}
                        placeholder="Add product types and press Enter"
                    />
                </div>
            );
        }
        
        // Special handling for applicableCountries in CouponCode - merged UI
        if (collectionName === 'CouponCode' && fieldName === 'applicableCountries') {
            return (
                <CountryCurrencyCombinedField 
                    countries={arrayValue}
                    currencies={allData.applicableCurrencies || []}
                    onChange={(newCountries, newCurrencies) => {
                        onChange(newCountries);
                        if (onUpdateField) {
                            onUpdateField('applicableCurrencies', newCurrencies);
                        }
                    }}
                    renderFieldHeader={renderFieldHeader}
                    readOnly={readOnly}
                />
            );
        }

        // Special handling for Sources profiles field — structured card editor
        if (fieldName === 'profiles' && (collectionName === 'Sources' || (arrayValue.length > 0 && arrayValue.every(item => typeof item === 'object' && item !== null && 'platform' in item)))) {
            return (
                <div>
                    {renderFieldHeader()}
                    <ProfilesEditor value={arrayValue} onChange={onChange} readOnly={readOnly} />
                </div>
            );
        }

        // Use TagInput for simple string arrays (like seoKeywords, socialLinks, episodeLinks)
        // Always use TagInput for these fields, or if array has string items
        if (fieldName === 'seoKeywords' || fieldName === 'socialLinks' || fieldName === 'episodeLinks' ||
            (arrayValue.length > 0 && arrayValue.every(item => typeof item === 'string'))) {
            return (
                <div>
                    {renderFieldHeader()}
                    <TagInput
                        value={arrayValue}
                        onChange={(newArray) => {
                            // Ensure we always pass an array, never null/undefined
                            onChange(Array.isArray(newArray) ? newArray : []);
                        }}
                        placeholder={
                            fieldName === 'socialLinks' ? "Add social link and press Enter" : 
                            fieldName === 'episodeLinks' ? "Add episode link (Apple Podcast, YouTube, Spotify, etc.) and press Enter" :
                            "Add item and press Enter"
                        }
                    />
                </div>
            );
        }

        // Use JSON editor for complex arrays
        return (
            <div>
                {renderFieldHeader()}
                <textarea
                    value={JSON.stringify(arrayValue, null, 2)}
                    onChange={(e) => {
                        try {
                            const parsed = JSON.parse(e.target.value);
                            if (Array.isArray(parsed)) {
                                setJsonError(null);
                                onChange(parsed);
                            } else {
                                setJsonError('Value must be an array');
                            }
                        } catch (err) {
                            const trimmed = e.target.value.trim();
                            if (trimmed === '' || trimmed === '[' || trimmed.startsWith('[')) {
                                setJsonError(null);
                            } else {
                                setJsonError(`Invalid JSON: ${err.message}`);
                            }
                        }
                    }}
                    rows={6}
                    style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: `1px solid ${jsonError || hasError ? '#dc2626' : '#d1d5db'}`,
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontFamily: 'monospace',
                        backgroundColor: jsonError || hasError ? '#fef2f2' : 'white',
                        lineHeight: '1.5',
                        resize: 'none'
                    }}
                    placeholder='Enter array as JSON, e.g., [{"key": "value"}]'
                />
                {jsonError && (
                    <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                        {jsonError}
                    </div>
                )}
                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                    Array with {arrayValue.length} item{arrayValue.length !== 1 ? 's' : ''}
                </div>
            </div>
        );
    }


    // Fallback - JSON editor
    return (
        <div>
            {renderFieldHeader()}
            <textarea
                value={JSON.stringify(value, null, 2)}
                onChange={(e) => {
                    try {
                        const parsed = JSON.parse(e.target.value);
                        onChange(parsed);
                    } catch {
                        // Keep as string if not valid JSON
                    }
                }}
                rows={4}
                style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: `1px solid ${hasError ? '#dc2626' : '#d1d5db'}`,
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontFamily: 'monospace',
                    backgroundColor: hasError ? '#fef2f2' : 'white',
                    resize: 'none'
                }}
                placeholder="Enter JSON data"
            />
        </div>
    );
}

